import React, { useState, useRef, useEffect } from 'react';
// @ts-ignore
import { CardData, Resource, PostIt, Action } from '../types';
import { useProjectStore } from '../store/useProjectStore';
import { useSimulationSafe } from '../context/SimulationContext';
import { useTheme } from '../context/ThemeContext';
import { getHeatmapSoftColor, getHeatmapColor } from '../utils/heatmap';
import { AlertTriangle, ClipboardList, Target } from 'lucide-react';
import { getLayoutTheme } from '../utils/layoutColors';
import { ClassicProcessShape, ClassicExternalSource, FactoryProcessShape } from './ClassicShapes';
import { VsmCanvasShape } from './VsmCanvasShapes';
import { getEffectiveMetric } from '../utils/vsmGeometry';
import { BpCelula, BpDatabox } from './VsmIconsBlueprint';
import { isVsmCellProcessCard } from './Card/cardUtils';
import { formatResourcesDisplay, formatTimeDisplay, formatNumber } from './ActivityNode/formatUtils';
import { CARD_THEMES } from './ActivityNode/cardThemes';
import { 
  adjustColorBrightness, 
  isColorDark, 
  hexToRgb, 
  rgbToHsl, 
  hslToRgb, 
  rgbToHex,
  createPastelVariation,
  getDesaturatedBorder,
  getReadableTextColor,
  desaturateColor,
  increaseContrast,
  convertToPostItTone
} from './ActivityNode/colorUtils';
import type { ActivityNodeProps } from './ActivityNode/types';

// Constante para evitar nova referência de array vazio a cada render
const EMPTY_CARDS_ARRAY: CardData[] = [];

export const ActivityNode: React.FC<ActivityNodeProps> = ({
    // --- Overlay de drop/drag: variáveis para centralização perfeita ---
    // (declarar logo antes do return, não aqui)
  data,
  selected,
  isDragging,
  isConnecting,
  isBottleneck,
  analysisMode,
  opacity,
  cardWidth,
  cardHeight,
  onEdit,
  handlePointerDown,
  handlePointerMove,
  handlePointerUp,
  onPostItStartDrag,
  scale,
  onFinishConnection,
  linkedPostIts = [],
  onPostItClick,
  onPostItContextMenu,
  onPostItTextChange,
  onOpenInMemo,
  linkedActions = [],
  onShowActionSummary,
  onUpdateCardData,
  onOpenImpactPanel,
  viewMode = 'kaizen_work',
  heatmapMode = false,
  isKaizenMode = false,
  showPercentBadge = true,
  showAS = true,
  showFTE = true,
  liveMetrics = null,
}) => {

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(data.atividade || '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Estado para controlar quais post-its estão expandidos (clicados)
  const [expandedPostItIds, setExpandedPostItIds] = useState<Set<string>>(new Set());

  // ââ¬ââ¬ Simulação: consumir estado global ââ¬ââ¬
  const simulation = useSimulationSafe();
  
  // Estado para controlar quais post-its estão em modo de edição
  const [editingPostItIds, setEditingPostItIds] = useState<Set<string>>(new Set());
  
  // Estado local para texto dos post-its em edição (para evitar problemas de sincronização)
  const [editingTexts, setEditingTexts] = useState<Record<string, string>>({});



    const mapType = useProjectStore(state => state.mapType);
    const vsmDesignStyle = useProjectStore(state => state.vsmDesignStyle);
    const vsmCardColor = useProjectStore(state => state.vsmCardColor);
    const cards = useProjectStore(state => {
      const activeSheet = state.sheets.find(s => s.id === state.activeSheetId);
      return activeSheet?.cards ?? EMPTY_CARDS_ARRAY;
    });
    const effectiveLayoutType = (
      mapType === 'FACTORY' || data.vsmIcon
        ? 'classic'
        : (data.layoutType ?? 'default')
    ) as string;
    const { isDark, theme: appTheme } = useTheme();
    // Cores adaptadas ao tema escuro/midnight
    const themeStrokeColor = isDark ? '#ffffff' : '#000000';
    const themeCanvasBg = appTheme === 'black' ? '#131314' : isDark ? '#223244' : '#ffffff';

    // Estilos do Usuário (Office Style) - Declarar ANTES de usar
  const userBg = data.style?.backgroundColor;
  
  const isInitial = data.tipo === 'card_inicial';
  const isFinal = data.tipo === 'card_final';
  const isVsmEntity = !!data.vsmIcon && ['fornecedor', 'cliente', 'externo'].includes(data.vsmIcon);
  // Considerar como "ação" tanto o tipo específico quanto cards pintados de verde no layout post-it
  const isAction = data.tipo === 'acao' || (effectiveLayoutType === 'postit' && (
    data.layoutColor === 'emerald' ||
    data.layoutColor === 'green' ||
    data.layoutColor === 'green-10' ||
    data.layoutColor === 'green-20' ||
    data.layoutColor === 'green-dark' ||
    (userBg && (
      userBg.toLowerCase().includes('#bbf7d0') ||
      userBg.toLowerCase().includes('#86efac') ||
      userBg.toLowerCase().includes('green')
    ))
  ));
  const isRemoved = (data.status ?? '') === 'removido';
    const isFactoryProcess = mapType === 'FACTORY' && data.tipo === 'atividade' && (data as any).vsmIcon === 'processo';

    const toFactoryMinutes = (value: unknown, unit: unknown, fallbackUnit: 's' | 'min' | 'h' = 's') => {
      const numericValue = Number(value) || 0;
      const resolvedUnit = typeof unit === 'string' && unit.length > 0 ? unit : fallbackUnit;
      if (['dias', 'dia', 'd'].includes(resolvedUnit)) return numericValue * 1440;
      if (['horas', 'hora', 'h'].includes(resolvedUnit)) return numericValue * 60;
      if (['minutos', 'minuto', 'min'].includes(resolvedUnit)) return numericValue;
      return numericValue / 60;
    };

    const getFactoryDailyDemand = () => {
      const clientCard = cards.find(card => {
        const extType = String((card as any).extType ?? '').toUpperCase();
        return card.tipo === 'card_final' || extType === 'CLIENTE' || (card as any).vsmIcon === 'cliente';
      });
      const rawDemand = Number((clientCard as any)?.vsmDemanda ?? (clientCard as any)?.entrada ?? 0);
      const demandUnit = String((clientCard as any)?.unidadeDemanda ?? (clientCard as any)?.vsmDemandaUnidade ?? '/dia').toLowerCase();
      if (rawDemand <= 0) return 0;
      if (demandUnit.includes('mês') || demandUnit.includes('mes') || demandUnit.includes('/ms')) return rawDemand / 20;
      if (demandUnit.includes('hora')) return rawDemand * 8;
      return rawDemand;
    };

  const simIsActive = simulation?.isActive ?? simulation?.isSimulationMode ?? false;
  const simDemand = simulation?.demand ?? simulation?.simulatedDemand ?? 0;
  const shiftPreset = simulation?.shiftPreset ?? '1_shift';
  const simulationShiftMinutes =
    shiftPreset === '2_shifts' ? 960 : shiftPreset === 'plus_2h' ? 600 : 480;
  const simOverride = simulation?.kaizenOverrides?.[data.id];
  const vsmProjectionMetrics = ((data as any).vsmKaizenProjection?.metrics ?? {}) as Record<string, any>;
  const projectedCycleSeconds = Number(
    (simOverride as any)?.timeOverrideSeconds
    ?? (simOverride as any)?.timeOverride
    ?? vsmProjectionMetrics.cycleTime?.projectedSeconds
    ?? (data as any).vsmKaizenProjection?.projectedSeconds
    ?? 0,
  ) || 0;
  const baseFte = Math.max(1, Number(data.pessoasSimultaneas ?? 1));
  const simExtraFte = Math.max(0, Number(simOverride?.fte ?? simOverride?.extraFTE ?? 0));
  const simReworkReduction = Math.max(0, Math.min(100, Number(simOverride?.rework ?? simOverride?.reworkReduction ?? 0)));
  const baseProcessingMinutes = isFactoryProcess
    ? toFactoryMinutes((data as any).vsmCT ?? data.tp, (data as any).vsmCTUnit, 's')
    : Math.max(0, Number(data.tt ?? 0));
  const adjustedProcessingMinutes = projectedCycleSeconds > 0
    ? projectedCycleSeconds / 60
    : baseProcessingMinutes * (1 - simReworkReduction / 100);
  const nodeCapacityPerDay = adjustedProcessingMinutes > 0
    ? (simulationShiftMinutes * (baseFte + simExtraFte)) / adjustedProcessingMinutes
    : 0;
  const simulationLoadPercent = simIsActive && nodeCapacityPerDay > 0
    ? (simDemand / nodeCapacityPerDay) * 100
    : 0;
  const isSimOverloaded = simIsActive &&
    !isInitial &&
    !isFinal &&
    data.tipo !== 'decisao' &&
    simulationLoadPercent > 100;
  const simOccupancy = simIsActive
    ? (simulation?.getNodeOccupancy(data.id) ?? Math.round(simulationLoadPercent))
    : null;
  const simulationHeatmapClasses = (() => {
    if (!simIsActive || isInitial || isFinal || data.tipo === 'decisao') return '';
    // Enterprise: sem glow/ring dramático; resposta visual fica na borda/header semânticos
    return '';
  })();
  const isSimCrisis = simIsActive && simulationLoadPercent > 150;
  const bottleneckIntensity = isSimOverloaded ? Math.min(1, Math.max(0.25, (simulationLoadPercent - 100) / 80)) : 0;
  const bottleneckGlow = isSimOverloaded
    ? `0 0 ${Math.round(14 + bottleneckIntensity * 18)}px rgba(255, 0, 0, ${Math.min(0.95, 0.55 + bottleneckIntensity * 0.35)}), 0 0 0 2px rgba(220, 38, 38, ${Math.min(0.95, 0.55 + bottleneckIntensity * 0.35)})`
    : undefined;
  const bottleneckAnimation = isSimOverloaded && simulationLoadPercent > 125
    ? 'vsmCardBottleneckPulse 850ms ease-in-out infinite alternate'
    : undefined;
  
  // 🎯 LÓGICA DE VISUALIZAÇÕO: Aplicar estilo "fantasma" APENAS no modo kaizen_work
  const showGhostStyle = isRemoved && viewMode === 'kaizen_work';
  
  // ðŸâ¬Â¥ CÑÂLCULO DO HEATMAP LEVEL (Eficiência Lean)
  const getHeatmapLevel = () => {
    if (!heatmapMode || isInitial || isFinal) return null;
    if (isFactoryProcess) {
      const dailyDemand = getFactoryDailyDemand();
      const factoryTakt = dailyDemand > 0 ? 480 / dailyDemand : 0;
      const factoryCycleTime = toFactoryMinutes((data as any).vsmCT ?? data.tp, (data as any).vsmCTUnit, 's');

      if (factoryTakt <= 0 || factoryCycleTime <= 0) return null;

      const occupancy = (factoryCycleTime / factoryTakt) * 100;
      if (occupancy <= 85) return { level: 'excellent', color: 'bg-emerald-500', label: 'Dentro do takt', value: occupancy };
      if (occupancy <= 100) return { level: 'warning', color: 'bg-amber-500', label: 'Próximo do takt', value: occupancy };
      if (occupancy <= 130) return { level: 'poor', color: 'bg-orange-500', label: 'Acima do takt', value: occupancy };
      return { level: 'critical', color: 'bg-red-500', label: 'Gargalo crítico', value: occupancy };
    }
    if (mapType === 'FACTORY') return null;

    const tra = Number(data.tt) || 0;
    const start = cards.find(c => c.tipo === 'card_inicial');
    const unit = (start as any)?.unidadeDemanda || '/dia';
    const rawDemand = Number(start?.entrada ?? 0);
    const dailyDemand = unit === '/hora' ? rawDemand * 8 : unit === '/mês' ? rawDemand / 20 : rawDemand;
    const people = Math.max(1, Number(data.pessoasSimultaneas) || Number((data as any).p) || 1);
    if (dailyDemand <= 0 || tra <= 0) return null;

    const occupancy = (tra * dailyDemand) / (480 * people) * 100;
    if (occupancy <= 85) return { level: 'excellent', color: 'bg-emerald-500', label: 'Dentro do takt', value: occupancy };
    if (occupancy <= 100) return { level: 'warning', color: 'bg-amber-500', label: 'Próximo do takt', value: occupancy };
    if (occupancy <= 130) return { level: 'poor', color: 'bg-orange-500', label: 'Acima do takt', value: occupancy };
    return { level: 'critical', color: 'bg-red-500', label: 'Gargalo crítico', value: occupancy };
  };
  
  const heatmapLevel = getHeatmapLevel();

  // Sincronizar editValue quando data.atividade mudar externamente
  useEffect(() => {
    if (!isEditing) {
      setEditValue(data.atividade || '');
    }
  }, [data.atividade, isEditing]);

  // Focar e selecionar texto ao entrar em modo edição
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus({ preventScroll: true });
      
      // Auto-resize imediato
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;

      const value = textareaRef.current.value;
      // Selecionar tudo se for o texto padrão
      if (value === 'Nova Ação' || value === 'Nova Atividade' || value.length < 3) {
        textareaRef.current.select();
      } else {
        // Senão, colocar cursor no fim
        textareaRef.current.setSelectionRange(value.length, value.length);
      }
    }
  }, [isEditing]);

  // Focar post-its vinculados ao entrar em modo edição
  useEffect(() => {
    if (editingPostItIds.size > 0) {
      // Focar o último post-it que entrou em modo edição
      const latestId = Array.from(editingPostItIds).pop();
      if (latestId) {
        const el = document.getElementById(`editing-postit-${latestId}`);
        if (el instanceof HTMLTextAreaElement) {
          el.focus({ preventScroll: true });
        }
      }
    }
  }, [editingPostItIds]);

  const handleTextClick = (e: React.MouseEvent) => {
    if (isAction && !isRemoved && !isConnecting) {
      e.stopPropagation();
      setIsEditing(true);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setEditValue(newValue);
    if (onUpdateCardData) {
      onUpdateCardData(data.id, { atividade: newValue });
    }
    // Auto-resize
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      textareaRef.current?.blur();
    } else if (e.key === 'Escape') {
      setEditValue(data.atividade || '');
      setIsEditing(false);
    }
  };

  const renderEditableTitle = (currentText: string, style: React.CSSProperties, className: string = "") => {
    if (isEditing && isAction) {
      return (
        <textarea
          ref={textareaRef}
          value={editValue}
          onChange={handleTextChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
          className={`w-full bg-transparent border-none outline-none resize-none p-0 m-0 text-center font-bold overflow-hidden ${className}`}
          style={{ 
            ...style,
            display: 'block',
          }}
          rows={1}
        />
      );
    }
    return (
      <span 
        className={`${className} ${isAction && !isRemoved ? 'cursor-text hover:bg-black/5 rounded px-1 -mx-1 transition-colors' : ''}`}
        style={style}
        onClick={handleTextClick}
      >
        {currentText}
      </span>
    );
  };
  
  // Obter unidades configuradas (Sincronizado com RightPanel)
  const customUnitLT = (data as any).customUnitLT || data.tpUnit || 'm';
  const customUnitTT = (data as any).customUnitTT || data.traUnit || 'm';

  const simulatedFteTotal = baseFte + simExtraFte;
  const effectivePValue = simIsActive ? simulatedFteTotal : Number(data.pessoasSimultaneas ?? 0);
  const effectiveTraMinutes = simIsActive ? Math.max(0, adjustedProcessingMinutes) : Number(data.tt ?? 0);
  const isSimulatedPChanged = simIsActive && Math.abs(effectivePValue - baseFte) > 0.001;

  // Valores já estão em minutos no store
  const ltInMinutes = Number(data.lt || 0);
  const ttInMinutes = Number(effectiveTraMinutes || 0);
  
  // FASE 3.2: Análise de Gargalos - Calcular taktTime real com base na demanda
  const colorCardsBySector = useProjectStore(state => state.colorCardsBySector);
  
  // Calcular taktTime com base na demanda real do card inicial (com conversão de unidade)
  const startCard = cards.find(c => c.tipo === 'card_inicial');
  const startCardUnit: string = (startCard as any)?.unidadeDemanda || '/dia';
  const rawStartDemand = Number(startCard?.entrada ?? 0);
  const toDailyDemand = (val: number, unit: string): number => {
    if (unit === '/hora') return val * 8;
    if (unit === '/mês')  return val / 20;
    return val; // /dia
  };
  const realDemand = mapType === 'FACTORY' ? getFactoryDailyDemand() : toDailyDemand(rawStartDemand, startCardUnit) || 0;
  const availableTime = 480; // 8 horas em minutos
  const taktTime = realDemand > 0 ? availableTime / realDemand : 0;

  // Calcular maxLoad para o Heatmap (baseado em TRA ou Carga)
  const maxLoad = React.useMemo(() => {
    if (!heatmapMode) return 0;
    if (mapType === 'FACTORY') {
      const demand = getFactoryDailyDemand();
      const factoryTakt = demand > 0 ? 480 / demand : 0;
      if (factoryTakt <= 0) return 100;
      return Math.max(
        ...cards
          .filter(c => c.tipo === 'atividade' && (c as any).vsmIcon === 'processo')
          .map(c => (toFactoryMinutes((c as any).vsmCT ?? c.tp, (c as any).vsmCTUnit, 's') / factoryTakt) * 100),
        100
      );
    }
    return 100;
  }, [cards, heatmapMode, mapType]);
  
  // Usar LT em minutos para COMPARA!ÑO com taktTime (que está em min/pç)
  const tpInMinutes = ltInMinutes;
  
  // CORREÇÕO CRÑÂTICA: Usar TRA (Tempo de Realização) para ocupação, não TP (Lead Time)
  // No Lean Office, gargalos são identificados pelo tempo de EXECUÇÕO real, não pelo lead time total
  const factoryCycleTimeMinutes = isFactoryProcess ? toFactoryMinutes((data as any).vsmCT ?? data.tp, (data as any).vsmCTUnit, 's') : 0;
  const traInMinutes = isFactoryProcess ? factoryCycleTimeMinutes : (ttInMinutes || 0);
  const officePeople = Math.max(1, Number(data.pessoasSimultaneas) || Number((data as any).p) || 1);
  const officeOccupancy = realDemand > 0 ? (traInMinutes * realDemand) / (availableTime * officePeople) * 100 : 0;
  const heatmapValue = isFactoryProcess && taktTime > 0 ? (traInMinutes / taktTime) * 100 : officeOccupancy;
  const supportsHeatmap = heatmapMode && !isInitial && !isFinal && data.tipo !== 'decisao' && (mapType === 'FACTORY' ? isFactoryProcess : realDemand > 0 && traInMinutes > 0);
  
  // Heatmap Color
  const heatmapBg = supportsHeatmap
    ? getHeatmapSoftColor(heatmapValue, maxLoad)
    : undefined;

  const heatmapBorder = supportsHeatmap
    ? getHeatmapColor(heatmapValue, maxLoad)
    : '#e2e8f0'; // Cinza padrão quando heatmap OFF

  // BADGE DE CARGA: Sempre visível com cores semânticas
  const loadValue = parseFloat(String(data.carga || 0));
  
  let loadBadgeColor = 'bg-blue-100 text-blue-700 border-blue-200'; // Padrão (baixa carga)
  
  if (!isInitial && !isFinal && data.tipo !== 'decisao') {
    if (loadValue > 85) {
      loadBadgeColor = 'bg-red-500 text-white border-red-600'; // Crítico
    } else if (loadValue > 60) {
      loadBadgeColor = 'bg-amber-100 text-amber-700 border-amber-200'; // Atenção
    } else if (loadValue > 0) {
      loadBadgeColor = 'bg-emerald-100 text-emerald-700 border-emerald-200'; // Saudável/Baixo
    }
  }

  // Calcular % de ocupação e status de gargalo (para borda e análise)
  let bottleneckStatus: 'normal' | 'warning' | 'critical' = 'normal';
  let occupancyPercent = 0;
  let borderClass = 'border-gray-200';
  
  // TAKT TIME VISUAL: Cor de borda baseada na relação TRA vs Takt
  let taktBorderColor: string | undefined;
  let isTaktExceeded = false;
  let taktWarningLevel = 0; // 0=ok, 1=warning, 2=critical
  
  if (taktTime > 0 && traInMinutes > 0 && !isInitial && !isFinal && data.tipo !== 'decisao') {
    // CORRIGIDO: Compara TRA (tempo de execução) com Takt Time
    occupancyPercent = (traInMinutes / taktTime) * 100;
    
    if (traInMinutes > taktTime) {
      // GARGALO CRÑÂTICO: TRA > Takt Time (capacidade insuficiente)
      bottleneckStatus = 'critical';
      borderClass = 'border-red-500';
      taktBorderColor = '#ef4444'; // Vermelho
      isTaktExceeded = true;
      taktWarningLevel = 2;
    } else if (traInMinutes > taktTime * 0.85) {
      // ALERTA: TRA > 85% do Takt Time (próximo do limite)
      bottleneckStatus = 'warning';
      borderClass = 'border-yellow-500';
      taktBorderColor = '#eab308'; // Amarelo
      taktWarningLevel = 1;
    } else {
      // FLUXO OK: TRA <= 85% do Takt Time (capacidade adequada)
      bottleneckStatus = 'normal';
      borderClass = 'border-green-500';
      taktBorderColor = '#22c55e'; // Verde
      taktWarningLevel = 0;
    }
  }
  
  // WIP (Work in Progress) - Estoque de Conhecimento
  const wipCount = (data as any).wip ?? 0;

  // Estilos do Usuário (Office Style) - userBg já foi declarado acima
  const userTextColor = data.style?.textColor;
  const userBold = data.style?.isBold ? 'font-bold' : 'font-medium';
  const userItalic = data.style?.isItalic ? 'italic' : '';
  const userFontSize = data.style?.fontSize || null;

  // Forçar aplicação de cores do Ribbon em todos os modos
  // Mapa completo: 5 famílias Ñ 4 tons (do mais claro ao mais escuro)
  const ribbonColorBgMap: Record<string, string> = {
    // Cinza
    'white': '#ffffff',
    'gray-10': '#f3f4f6',
    'gray-20': '#e5e7eb',
    'gray': '#e5e7eb',
    // Amarelo
    'yellow-10': '#fefce8',
    'yellow-20': '#fef9c3',
    'yellow': '#fef08a',
    'yellow-dark': '#fde047',
    // Vermelho
    'red-10': '#fef2f2',
    'red-20': '#fee2e2',
    'red': '#fecaca',
    'red-dark': '#fca5a5',
    // Verde
    'green-10': '#ecfdf5',
    'green-20': '#d1fae5',
    'green': '#bbf7d0',
    'green-dark': '#86efac',
    // Azul
    'blue-10': '#eff6ff',
    'blue-20': '#dbeafe',
    'blue': '#bfdbfe',
    'blue-dark': '#93c5fd',
    // Legado (nomes antigos)
    'emerald': '#ecfdf5',
    'ocean': '#eff6ff',
    'orange': '#fff7ed',
    'amber': '#fefce8',
    'coral': '#fef2f2',
    'purple': '#faf5ff',
    'lavender': '#faf5ff',
    'forest': '#f0fdf4',
    'sage': '#f0fdf4',
    'sunset': '#fff7ed',
    'midnight': '#f1f5f9',
  };
  const ribbonColorBg = data.layoutColor
    ? (data.layoutColor.startsWith('#') ? data.layoutColor : ribbonColorBgMap[data.layoutColor])
    : undefined;

  // No modo clássico, usar cores do CARD_THEMES se layoutColor estiver definido, senão usar userBg
  let classicBg = effectiveLayoutType === 'classic' 
    ? (data.layoutColor && CARD_THEMES[data.layoutColor] ? CARD_THEMES[data.layoutColor].headerBg : (ribbonColorBg || userBg))
    : userBg;
  // Em modo escuro, sem cor de usuário definida, usar fundo do canvas do tema
  if (isDark && !classicBg) {
    classicBg = themeCanvasBg;
  }

  // Cores Padrão - Enterprise Premium Style
  let defaultBgClass = ribbonColorBg ? `bg-[${ribbonColorBg}]` : 'bg-white';
  
  // 🎨 COR DE BORDA: Sempre usar cor personalizada do usuário
  // Heatmap NÕO altera cores de borda - apenas mostra/esconde badges
  const userBorderColorHex = data.layoutColor?.startsWith('#')
    ? (data.layoutColor !== '#ffffff' ? data.layoutColor : '#10b981') // hex direto do color picker
    : (data.layoutColor && CARD_THEMES[data.layoutColor]
        ? CARD_THEMES[data.layoutColor].border
        : '#10b981'); // Verde padro

  // Estado semântico do contorno (somente quando simulação está ativa)
  const supportsSemanticState = !isInitial && !isFinal && data.tipo !== 'decisao';
  const semanticBorderVisual = (() => {
    if (!supportsSemanticState || !simIsActive) {
      return { color: userBorderColorHex, width: '2px' };
    }

    // Regra de intensidade restaurada por nível de carga da simulação
    if (simulationLoadPercent > 150) {
      return { color: '#DC2626', width: '3px' };
    }
    if (simulationLoadPercent > 100) {
      return { color: '#F59E0B', width: '2.5px' };
    }
    if (simulationLoadPercent >= 90) {
      return { color: '#F59E0B', width: '2px' };
    }

    return { color: userBorderColorHex, width: '2px' };
  })();

  // Cores sutis baseadas no tipo: Processo = Azul claro, Decisão = Roxo claro
  let headerClass = 'bg-blue-50 text-slate-700'; // Azul claro para processos

  if (!userBg) {
    switch (data.tipo) {
      case 'card_inicial':
      case 'card_final': {
        defaultBgClass = 'bg-white';
        headerClass = 'bg-emerald-500 text-white';
        // Cards inicial/final sempre usam borda verde quando não estão em heatmap
        break;
      }
      case 'atividade':
      case 'acao': {
        defaultBgClass = 'bg-white';
        headerClass = data.tipo === 'acao' ? 'bg-emerald-500 text-white' : 'bg-blue-50 text-slate-700'; // Verde para açÑÂµes, Azul claro para processos
        break;
      }
    }
  }

  // Normalizar layoutColor: converter formatos especiais
  const normalizeColorName = (color: string | undefined): string | undefined => {
    if (!color) return undefined;

    // Converter formatos especiais (color-dark -> darkcolor)
    // Manter sufixos numéricos (-10, -20) intactos para diferenciar tons
    const colorMap: Record<string, string> = {
      'grey': 'gray',
      'red-dark': 'darkred',
      'blue-dark': 'darkblue',
      'green-dark': 'darkgreen',
      'yellow-dark': 'darkyellow',
      'blue-light': 'lightblue',
      'green-light': 'lightgreen',
    };

    return colorMap[color] || color;
  };

  const cleanLayoutColor = normalizeColorName(data.layoutColor);

  // Obter tema de cores do layout (se houver)
  const layoutTheme = effectiveLayoutType && cleanLayoutColor && (effectiveLayoutType === 'default' || effectiveLayoutType === 'postit' || effectiveLayoutType === 'minimal')
    ? getLayoutTheme(effectiveLayoutType, cleanLayoutColor)
    : null;
  
  // Cores padrão para fallback
  const defaultPostItTheme = {
    bg: '#fff9c4',
    border: '#e3d081',
    footerBg: '#fff59d',
    text: '#1f2937',
    textSecondary: '#64748b',
  };
  
  const defaultDefaultTheme = {
    bg: '#ffffff',
    border: '#10b981',
    headerBg: '#10b981',
    text: '#1f2937',
    textSecondary: '#64748b',
  };
  
  const defaultMinimalTheme = {
    bg: '#f0f9ff',
    border: '#0ea5e9',
    text: '#0c4a6e',
    textSecondary: '#0284c7',
    accent: '#38bdf8',
  };
  
  // Tema atual baseado no layout (com fallback)
  const postItTheme = effectiveLayoutType === 'postit' 
    ? (layoutTheme || defaultPostItTheme)
    : defaultPostItTheme;
  
  // Cores para o modo Post-it (com layoutColor ou userBg)
  // PRIORIDADE: layoutColor (do ribbon) > userBg (do Office Style)
  let postItBgColor: string | undefined;
  let postItBorderColor: string | undefined;
  let postItFooterBg: string | undefined;
  let postItTextColor: string | undefined;
  
  if (effectiveLayoutType === 'postit') {
    // Se tem layoutColor definido (seleção do ribbon), usar o tema
    if (data.layoutColor && layoutTheme) {
      const isWhiteColor = data.layoutColor === 'white';
      
      if (isWhiteColor) {
        // Cor branca: fundo branco, borda preta
        postItBgColor = '#ffffff';
        postItBorderColor = '#000000';
        postItFooterBg = '#e2e8f0'; // Cinza claro para P/TP/TRA
        postItTextColor = '#000000';
      } else {
        // Outras cores: usar tema do layoutColor
        postItBgColor = layoutTheme.bg;
        postItBorderColor = layoutTheme.border;
        postItFooterBg = layoutTheme.footerBg || layoutTheme.bg;
        postItTextColor = layoutTheme.text || '#1f2937';
      }
    }
    // Se não tem layoutColor mas tem userBg (Office Style), processar userBg
    else if (userBg) {
      const bgColor = userBg.toLowerCase().trim();
      const isWhite = bgColor === '#ffffff' || bgColor === '#fff' || bgColor === 'white' || bgColor === 'rgb(255, 255, 255)';
      const isYellow = bgColor === '#eab308' || bgColor === '#fbbf24' || bgColor === '#facc15' || bgColor === '#fff9c4' || bgColor === '#fefce8' || bgColor.includes('yellow');
      
      if (isWhite) {
        postItBgColor = '#ffffff';
        postItBorderColor = '#000000';
        postItFooterBg = '#e2e8f0';
      } else if (isYellow) {
        postItBgColor = postItTheme.bg;
        postItBorderColor = postItTheme.border;
        postItFooterBg = postItTheme.footerBg || postItTheme.bg;
      } else {
        const postItTone = convertToPostItTone(userBg);
        postItBgColor = postItTone.bg;
        postItBorderColor = postItTone.border;
        postItFooterBg = postItTone.footer;
      }
    }
    // Fallback para tema padrão
    else {
      postItBgColor = postItTheme.bg;
      postItBorderColor = postItTheme.border;
      postItFooterBg = postItTheme.footerBg || postItTheme.bg;
    }
  }
  
  const defaultTheme = effectiveLayoutType === 'default'
    ? (layoutTheme || defaultDefaultTheme)
    : defaultDefaultTheme;
  
  const minimalTheme = effectiveLayoutType === 'minimal'
    ? (layoutTheme || defaultMinimalTheme)
    : defaultMinimalTheme;

  // --- GESTÕO VISUAL DINÑaMICA POR SETOR (Fase 3.1) ---
  // Identificar recurso principal e setor correspondente
  const organization = useProjectStore(state => state.organization);
  const postItsViewMode = useProjectStore(state => state.postItsViewMode);

  // Subscricao direta aos campos visuais do card na store â€” re-render imediato sem esperar prop cascade
  const liveCard = useProjectStore(state => {
    const sheet = state.sheets.find(s => s.id === state.activeSheetId);
    return sheet?.cards.find(c => c.id === data.id) ?? null;
  });
  const effectiveSector = (liveCard as any)?.sector || (liveCard as any)?.setor || (data as any).sector || (data as any).setor || '';
  // Campos de display: preferir liveCard (store), fallback para prop data
  const effectiveAtividade = liveCard?.atividade ?? data.atividade ?? '';
  const effectiveVsmTitle = String(effectiveAtividade).replace(/^\s*\d+\s+(?=\S)/, '').trim();
  const effectiveNomeFluxo = liveCard?.nome_fluxo ?? data.nome_fluxo ?? '';
  const effectiveTipoAtividade = (liveCard as any)?.tipo_atividade ?? (data as any).tipo_atividade ?? null;
  const effectivePercentLivreErros = (liveCard as any)?.percentLivreErros ?? (data as any).percentLivreErros ?? 100;
  const effectiveStatus = liveCard?.status ?? data.status ?? '';
  const effectiveComo = (liveCard as any)?.como ?? (data as any).como ?? '';
  const effectiveSistema = (liveCard as any)?.sistema ?? (data as any).sistema ?? '';
  const effectiveEntrada = liveCard?.entrada ?? data.entrada;
  const effectiveUnidadeDemanda = (liveCard as any)?.unidadeDemanda ?? (data as any).unidadeDemanda ?? '/dia';

  // Sincronizar editValue com o valor ao vivo da store (se nao estiver editando)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!isEditing) {
      setEditValue(effectiveAtividade || '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveAtividade]);
  
  let mainResource: Resource | null = null;
  if (data.resources && data.resources.length > 0) {
    if (data.resources.length === 1) {
      mainResource = data.resources[0];
    } else {
      // Escolher recurso com maior "peso" (quantity * dedication)
      mainResource = data.resources.reduce((best, current) => {
        const bestWeight = (best.quantity || 0) * ((best.dedication ?? 100) / 100);
        const currentWeight = (current.quantity || 0) * ((current.dedication ?? 100) / 100);
        return currentWeight > bestWeight ? current : best;
      }, data.resources[0]);
    }
  }

  let sectorColor: string | undefined;
  let sectorName: string | undefined;

  // 1. Lookup via recurso   cargo   setor (mais preciso)
  if (mainResource && organization && organization.roles && organization.departments) {
    const role = organization.roles.find(r => r.id === mainResource!.roleId);
    if (role) {
      const dept = organization.departments.find(d => d.id === role.departmentId);
      if (dept) {
        sectorColor = dept.color;
        sectorName = dept.name;
      }
    }
  }

  // 2. Fallback via data.sector (campo Setor do card, salvo pelo PropertiesPanel)
  // Usa effectiveSector (subscrição direta na store) para atualizar sem esperar prop cascade
  if (!sectorColor && effectiveSector && organization?.departments) {
    const dept = organization.departments.find(d => d.name === effectiveSector);
    if (dept) {
      sectorColor = dept.color;
      sectorName = dept.name;
    }
  }

  // 3. Fallback via layoutColor hex (gravado ao atribuir recurso)
  if (!sectorColor && data.layoutColor && data.layoutColor.startsWith('#')) {
    sectorColor = data.layoutColor;
  }

  // Lógica de Background - SEMPRE branco no modo default
  // Removido: userBg não deve pintar o fundo interno do card
  let bgStyle = {};

  // Tint de cor do setor sobre o card (quando colorCardsBySector ativo)
  const sectorBg = colorCardsBySector && sectorColor ? sectorColor + '66' : undefined;
  // Classic: aplicar sectorColor apenas quando toggle está ativo
  if (colorCardsBySector && sectorColor) classicBg = sectorColor;

  // Postit: bordas seguem a cor ativa do card, 30% dessaturada
  if (effectiveLayoutType === 'postit') {
    const activeCardColor = (colorCardsBySector ? sectorColor : undefined) || ribbonColorBg || postItBgColor;
    if (activeCardColor && activeCardColor !== '#ffffff') {
      postItBorderColor = adjustColorBrightness(activeCardColor, -30);
    }
  }

  // Smart text color: detecta fundo escuro e troca automaticamente para branco/preto
  const postItEffectiveBg = (colorCardsBySector ? sectorColor : undefined) || ribbonColorBg || postItBgColor || '#ffffff';
  const postItSmartText = getReadableTextColor(postItEffectiveBg);
  const postItSmartTextDim = postItSmartText === '#ffffff' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.5)';

  const minimalAccentColor = ribbonColorBg || (colorCardsBySector ? sectorColor : undefined) || layoutTheme?.accent || minimalTheme.accent || minimalTheme.border || '#3b82f6';
  const minimalTitleSmartText = minimalAccentColor ? getReadableTextColor(minimalAccentColor) : '#ffffff';
  // Card body bg is the toLight version (88% white blend)  always light  always dark text
  const minimalBodySmartText = '#000000';
  const minimalBodySmartTextDim = 'rgba(0,0,0,0.5)';
  const minimalAccentSmartTextDim = minimalTitleSmartText === '#ffffff' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.5)';
  const bodyLinesSvg = encodeURIComponent("<svg xmlns='http://www.w3.org/2000/svg' width='8' height='8'><line x1='0' y1='8' x2='8' y2='0' stroke='rgba(0,0,0,0.04)' stroke-width='0.5'/></svg>");
  const bodyLinesUrl = "url(data:image/svg+xml," + bodyLinesSvg + ")";
  const frameLinesSvg = encodeURIComponent("<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10'><line x1='0' y1='10' x2='10' y2='0' stroke='rgba(255,255,255,0.18)' stroke-width='1'/><line x1='-2' y1='2' x2='2' y2='-2' stroke='rgba(255,255,255,0.18)' stroke-width='1'/><line x1='8' y1='12' x2='12' y2='8' stroke='rgba(255,255,255,0.18)' stroke-width='1'/></svg>");
  const frameLinesUrl = data.layoutColor !== 'white' && !heatmapBg ? ("url(data:image/svg+xml," + frameLinesSvg + ")") : undefined;

  // Classic smart text: adapts to dark backgrounds
  const classicEffectiveBg = classicBg || '#ffffff';
  const classicSmartText = getReadableTextColor(classicEffectiveBg);
  const classicSmartTextDim = classicSmartText === '#ffffff' ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)';

  // Container principal invisível (sem overflow, borda ou cor de fundo)
  return (
    <div
      className={`flex flex-col items-center ${!isVsmEntity && ((isFinal && effectiveLayoutType !== 'classic') || (isInitial && effectiveLayoutType !== 'classic')) ? 'rounded-full overflow-hidden' : 'rounded'} transition-all duration-300`}
      style={{ width: (isInitial || isFinal) && effectiveLayoutType === 'classic' && !(isVsmEntity && isFinal) ? '220px' : `${cardWidth}px`, overflow: !isVsmEntity && ((isFinal && effectiveLayoutType !== 'classic') || (isInitial && effectiveLayoutType !== 'classic')) ? 'hidden' : 'visible' }}
      data-layout={effectiveLayoutType}
      data-tipo={data.tipo}
      onPointerDown={handlePointerDown as React.PointerEventHandler<HTMLDivElement>}
      onPointerMove={handlePointerMove as React.PointerEventHandler<HTMLDivElement>}
      onPointerUp={handlePointerUp}
      onClick={(e) => { 
        if(isConnecting) { 
          e.stopPropagation(); 
          e.preventDefault();
          onFinishConnection(data.id); 
        }
      }}
    >
      {/* Card Principal - Isolado */}
      <div 
        className={`w-full relative z-20 ${!isVsmEntity && ((isFinal && effectiveLayoutType !== 'classic') || (isInitial && effectiveLayoutType !== 'classic')) ? 'rounded-full' : 'rounded'}`}
        style={{ 
          height: `${cardHeight}px`,
          background: 'transparent',
          boxShadow: 'none',
          border: 'none',
          overflow: !isVsmEntity && ((isFinal && effectiveLayoutType !== 'classic') || (isInitial && effectiveLayoutType !== 'classic')) ? 'hidden' : 'visible',
        }}
      >
        {/* BADGE CANTO SUPERIOR DIREITO - Removido: ID e Ocupação agora integrados ao header interno (Enterprise UI) */}
        {/* Manter apenas badge Kaizen externo (modo especial) */}
        {!isInitial && !isFinal && data.tipo !== 'decisao' && isKaizenMode && (data as any).acoes_melhoria && (data as any).acoes_melhoria.length > 0 && (
          <div className="absolute -top-3 -right-2 z-50 flex items-center gap-1">
            <div className="bg-emerald-100 text-emerald-700 font-bold text-[10px] px-2 py-0.5 rounded-sm shadow-sm border border-emerald-200 flex items-center gap-1">
              âÅ¡Â¡ {(data as any).acoes_melhoria.length} {(data as any).acoes_melhoria.length === 1 ? 'Melhoria' : 'Melhorias'}
            </div>
          </div>
        )}
        
        {/* BADGE WIP (ESTOQUE DE CONHECIMENTO) - Canto superior esquerdo */}
        {!isInitial && !isFinal && data.tipo !== 'decisao' && wipCount > 0 && (
          <div 
            className="absolute -top-2 -left-2 z-50 flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm shadow-sm border"
            style={{
              backgroundColor: wipCount > 10 ? '#fef2f2' : wipCount > 5 ? '#fffbeb' : '#f0fdf4',
              borderColor: wipCount > 10 ? '#fca5a5' : wipCount > 5 ? '#fcd34d' : '#86efac',
              color: wipCount > 10 ? '#dc2626' : wipCount > 5 ? '#d97706' : '#16a34a'
            }}
            title={`${wipCount} itens na fila (WIP)`}
          >
            <span className="text-[9px]">📥</span>
            <span className="text-[10px] font-bold">{wipCount}</span>
          </div>
        )}

        {/* BADGE AO VIVO - Mapa Vivo (live workflow metrics) */}
        {liveMetrics && !isInitial && !isFinal && data.tipo !== 'decisao' && (
          <div
            className="absolute -bottom-2 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 px-1.5 py-0.5 rounded-sm shadow-sm border text-[8px] font-semibold whitespace-nowrap"
            style={{
              backgroundColor:
                liveMetrics.slaStatus === 'breach'
                  ? '#fef2f2'
                  : liveMetrics.slaStatus === 'warning'
                  ? '#fffbeb'
                  : '#f0fdf4',
              borderColor:
                liveMetrics.slaStatus === 'breach'
                  ? '#fca5a5'
                  : liveMetrics.slaStatus === 'warning'
                  ? '#fcd34d'
                  : '#86efac',
              color:
                liveMetrics.slaStatus === 'breach'
                  ? '#dc2626'
                  : liveMetrics.slaStatus === 'warning'
                  ? '#d97706'
                  : '#16a34a',
            }}
            title={`Ao vivo: ${liveMetrics.activeTasks} ativas, ${liveMetrics.completedTasks} concluídas${liveMetrics.avgDurationMinutes !== null ? `, avg ${Math.round(liveMetrics.avgDurationMinutes)}m` : ''}`}
          >
            <span
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{
                backgroundColor:
                  liveMetrics.slaStatus === 'breach'
                    ? '#dc2626'
                    : liveMetrics.slaStatus === 'warning'
                    ? '#d97706'
                    : '#16a34a',
              }}
            />
            {liveMetrics.activeTasks > 0 ? (
              <span>{liveMetrics.activeTasks} ativa{liveMetrics.activeTasks !== 1 ? 's' : ''}</span>
            ) : (
              <span>{liveMetrics.completedTasks} concl.</span>
            )}
            {liveMetrics.avgDurationMinutes !== null && (
              <span>· {liveMetrics.avgDurationMinutes < 60 ? `${Math.round(liveMetrics.avgDurationMinutes)}m` : `${(liveMetrics.avgDurationMinutes / 60).toFixed(1)}h`}</span>
            )}
          </div>
        )}

        {/* DESPERDÑÂCIOS */}
        {!simIsActive && !isInitial && data.desperdicios && data.desperdicios.length > 0 && (
          <div className="absolute -top-2 -right-2 flex flex-col gap-1 z-40">
            {data.desperdicios.slice(0, 2).map(w => (
              <div key={w} className="w-5 h-5 bg-red-100 text-red-600 rounded-full border border-white shadow-sm flex items-center justify-center" title={w}><AlertTriangle size={10}/></div>
            ))}
            {data.desperdicios.length > 2 && <div className="w-5 h-5 bg-slate-100 text-slate-600 rounded-full border border-white shadow-sm flex items-center justify-center text-[9px] font-bold">+{data.desperdicios.length-2}</div>}
          </div>
        )}

        {/* Overlay de drop/drag: CSS centralizado, herda border-radius do pai */}
        {isConnecting && !data.vsmIcon && (
          <div
            className="absolute inset-0 z-50 pointer-events-none rounded-[inherit] flex items-center justify-center"
            style={{
              border: '2.5px dashed #f59e42',
              backgroundColor: 'rgba(251,191,36,0.08)',
              boxShadow: '0 0 0 4px rgba(251,191,36,0.18), 0 0 16px rgba(251,191,36,0.10)',
            }}
          >
            <span className="text-yellow-600 font-bold animate-bounce" style={{ fontSize: 18 }}>✓</span>
          </div>
        )}

      {/* RENDERIZAÇÕO DO CARD */}
      {/* Modo Clássico (VSM) */}
      {effectiveLayoutType === 'classic' ? (
        <div
          className={`w-full h-full flex flex-col items-center justify-center relative ${simulationHeatmapClasses}`}
          style={{
            padding: 0,
            margin: 0,
            backgroundColor: 'transparent',
            justifyContent: isVsmEntity ? 'flex-start' : 'center',
            overflow: 'visible',
            pointerEvents: 'none',
            opacity: isRemoved ? 0.4 : 1,
            filter: isRemoved ? 'grayscale(100%)' : (isConnecting && data.vsmIcon ? 'drop-shadow(0 0 5px #f59e42) drop-shadow(0 0 2px #f97316)' : 'none'),
            transition: simIsActive ? 'box-shadow 300ms ease, border-color 300ms ease' : undefined,
          }}
        >
          {data.vsmIcon ? (() => {
            const isFactory = mapType === 'FACTORY';
            const isProcesso = data.vsmIcon === 'processo' && isFactory;
            const isEstoque = ['estoque', 'pulmao', 'supermercado'].includes(data.vsmIcon);
            const isExternal = ['fornecedor', 'cliente', 'externo'].includes(data.vsmIcon);
            const isEntrega = data.vsmIcon === 'entrega';
            const isProduto = data.vsmIcon === 'produto';
            const isVerticalProduto = isProduto && (data as any).vsmOrientation === 'vertical';
            const isTransport = ['caminhao', 'transporte'].includes(data.vsmIcon);
            const isDockedTransport = isTransport && !!(data as any).transportDockParentId;
            const isVerticalFlow = data.vsmIcon === 'vertical_flow';
            const isCommunication = data.vsmIcon === 'comunicacao';
            const isPcpm = data.vsmIcon === 'pcpm' || data.vsmIcon === 'info_eletro';
            const isNivelamento = data.vsmIcon === 'nivelamento';
            const isKanbanPosto = data.vsmIcon === 'kanban_posto';
            const isBlueprintVsm = vsmDesignStyle === 'blueprint';
            const isSketchVsm = vsmDesignStyle === 'sketch';
            // V221: estilo técnico — reutiliza geometria do blueprint com tinta preta limpa
            const isTechnicalVsm = vsmDesignStyle === 'technical';
            const isVectorVsm = isBlueprintVsm || isTechnicalVsm;
            const techInk = isDark ? '#e5e7eb' : '#1a1a1a';

            const vsmCT   = (data as any).vsmCT;
            const vsmCTUnit = (data as any).vsmCTUnit ?? 's';
            const vsmCO   = (data as any).vsmCO;
            const vsmDisp = (data as any).vsmDisp;
            const vsmOp   = (data as any).vsmOp;
            const vsmPerfil = (data as any).vsmPerfil ?? 'Dedicado';
            const hasVsmCT = Number(vsmCT) > 0;
            const hasVsmCO = Number(vsmCO) > 0;
            const hasVsmDisp = Number(vsmDisp) > 0;
            const inventory  = (data as any).inventory;
            const vsmDias = (data as any).vsmDias;
            const vsmUnit    = (data as any).vsmUnit;
            const hasDataBox = isProcesso && (hasVsmCT || hasVsmCO || hasVsmDisp);
            const inventoryMode = (data as any).inventoryMode;
            const showInventoryCoverage = inventoryMode === 'coverage' || (vsmDias != null && !inventory);
            const vsmDemanda = (data as any).vsmDemanda || (data as any).entrada;
            const vsmNome = (data as any).vsmNome;
            const vsmProduto = (data as any).vsmProduto;
            const vsmFrequencia = (data as any).vsmFrequencia;
            const vsmDemandMix: Array<{label:string;qty:number}> = (data as any).vsmDemandMix ?? [];
            const externalDataLines = [
              vsmProduto,
              vsmFrequencia,
              vsmDemanda ? `${vsmDemanda} pç/dia` : '',
              (data as any).vsmLinha4,
              (data as any).vsmLinha5,
            ].filter((value) => String(value ?? '').trim() !== '' && String(value ?? '').trim() !== '0');
            const externalDataRows = externalDataLines.length > 0
              ? externalDataLines.concat(Array(Math.max(0, 3 - externalDataLines.length)).fill(''))
              : [];
            const hasExternalData = externalDataLines.length > 0 || vsmDemandMix.some(v => v.qty > 0);
            const canvasBg = appTheme === 'black' ? '#131314' : isDark ? '#223244' : 'white';

            if (isVerticalFlow) {
              const arrowWidth = cardWidth || 32;
              const arrowHeight = Math.max(120, cardHeight || Number((data as any).vsmFlowHeight) || 220);
              // Tronco preenche ~60% da bbox; cabeça quase toda a largura — bbox tight match visual.
              const shaftLeft = Math.round(arrowWidth * 0.20);
              const shaftRight = Math.round(arrowWidth * 0.80);
              const headLeft = 2;
              const headRight = arrowWidth - 2;
              const headTop = Math.max(52, arrowHeight - 76);
              const points = [
                `${shaftLeft},2`,
                `${shaftRight},2`,
                `${shaftRight},${headTop}`,
                `${headRight},${headTop}`,
                `${Math.round(arrowWidth / 2)},${arrowHeight - 4}`,
                `${headLeft},${headTop}`,
                `${shaftLeft},${headTop}`,
              ].join(' ');

              return (
                <div style={{ position: 'relative', width: arrowWidth, height: arrowHeight, background: 'transparent' }}>
                  <svg
                    width={arrowWidth}
                    height={arrowHeight}
                    viewBox={`0 0 ${arrowWidth} ${arrowHeight}`}
                    aria-hidden="true"
                    style={{ position: 'absolute', inset: 0, overflow: 'visible' }}
                  >
                    <polygon
                      points={points}
                      fill={isTechnicalVsm ? canvasBg : canvasBg}
                      stroke={isTechnicalVsm ? techInk : isBlueprintVsm ? (vsmCardColor || '#06b6d4') : themeStrokeColor}
                      strokeWidth={isVectorVsm ? '1.6' : '3'}
                      strokeLinejoin="miter"
                      strokeLinecap="square"
                      vectorEffect="non-scaling-stroke"
                    />
                  </svg>
                  {selected && (
                    <div style={{ position: 'absolute', inset: -3, border: '1px solid #2563eb', pointerEvents: 'none' }} />
                  )}
                </div>
              );
            }

            if (isPcpm) {
              const title = 'PCPM';
              const vsmMetodo: string = (data as any).vsmMetodo ?? '';
              const isLocked: boolean = !!(data as any).vsmLocked;
              const pcpmWidth = cardWidth || 127;
              const pcpmHeight = 90;
              return (
                <div style={{ position: 'relative', width: pcpmWidth, height: pcpmHeight, backgroundColor: 'transparent', pointerEvents: 'none' }}>
                  <VsmCanvasShape
                    vsmIcon="processo"
                    width={pcpmWidth}
                    height={pcpmHeight}
                    selected={selected}
                  />
                  <div style={{ position: 'absolute', top: isVectorVsm ? '2.6%' : 0, left: 4, right: 4, height: isVectorVsm ? '33.1%' : '22%', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'auto', overflow: 'hidden' }}>
                    <span style={{ fontSize: 12, fontWeight: 900, color: themeStrokeColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', lineHeight: 1 }}>
                      {title}
                    </span>
                  </div>
                  {/* MRP badge + bloqueio clicável */}
                  <div
                    title={isLocked ? 'Sistema Puxado — clique para Empurrado' : 'Sistema Empurrado — clique para Puxado'}
                    onClick={(e) => { e.stopPropagation(); onUpdateCardData?.(data.id, { vsmLocked: !isLocked } as any); }}
                    style={{ position: 'absolute', right: 7, bottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, minWidth: 26, height: 18, pointerEvents: 'auto', cursor: 'pointer' }}
                  >
                    <span style={{ fontSize: 10, fontWeight: 800, color: isLocked ? '#dc2626' : themeStrokeColor, opacity: isLocked ? 0.4 : 0.86, whiteSpace: 'nowrap', textDecoration: isLocked ? 'line-through' : 'none', lineHeight: 1 }}>
                      {vsmMetodo || 'MRP'}
                    </span>
                    {isLocked && (
                      <svg width={16} height={16} viewBox="0 0 16 16" style={{ flexShrink: 0, pointerEvents: 'none' }}>
                        <circle cx={8} cy={8} r={7} fill="none" stroke="#dc2626" strokeWidth={1.8} />
                        <line x1={12} y1={3} x2={4} y2={13} stroke="#dc2626" strokeWidth={2} strokeLinecap="round" />
                      </svg>
                    )}
                  </div>
                </div>
              );
            }

            if (isCommunication) {
              const label = effectiveVsmTitle || effectiveAtividade || 'Comunicação';
              const width = cardWidth || 118;
              const height = cardHeight || 40;
              const borderColor = isDark ? '#cbd5e1' : '#111827';
              const bgColor = isDark ? '#1f2937' : '#ffffff';
              return (
                <div
                  style={{
                    position: 'relative',
                    width,
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '4px 10px',
                    background: bgColor,
                    border: `1.5px solid ${borderColor}`,
                    borderRadius: 3,
                    color: themeStrokeColor,
                    pointerEvents: 'auto',
                    boxShadow: selected ? '0 0 0 2px #2563eb' : 'none',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: 10,
                      fontWeight: 700,
                      lineHeight: 1.12,
                      textAlign: 'center',
                      whiteSpace: 'pre-line',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    } as React.CSSProperties}
                  >
                    {label}
                  </span>
                </div>
              );
            }

            /* ── PROCESSO + DATABOX: render unified SVG when data box is present ── */
            if (isProcesso && hasDataBox) {
              // Processo portion follows the real SVG divider: y=104 in a 207px viewBox.
              // Name bar: 0%–15% | Operator area: below the header, inside the 90px process body.
              // Row 1 center: ~55.3% | Row 2 center: ~66.2% | Row 3 center: ~80.5%
              const isCellProcess = isVsmCellProcessCard(data);
              const pdWidth = isCellProcess ? 230 : 121;
              const processBodyHeight = 90;
              const processBodyRatio = 104 / 207;
              const totalH = Math.round(processBodyHeight / processBodyRatio);
              const operatorInset = 7;
              const dataBoxFontSize = 12;
              const overlayFontFamily = isTechnicalVsm ? 'Inter, "Segoe UI", system-ui, sans-serif' : undefined;
              const rowStyle = (top: string): React.CSSProperties => ({
                position: 'absolute',
                top,
                left: 0,
                right: 0,
                height: '10.5%',
                display: 'flex',
                alignItems: 'center',
                fontSize: dataBoxFontSize,
                lineHeight: 1.08,
                pointerEvents: 'none',
                transform: 'translateY(-50%)',
                fontFamily: overlayFontFamily,
              });
              // Andon bottleneck: CT (seconds) > taktTime (minutes)
              const ctSeconds = vsmCT ? (vsmCTUnit === 'min' ? Number(vsmCT) * 60 : Number(vsmCT)) : 0;
              const kaizenMetrics = ((data as any).vsmKaizenProjection?.metrics ?? {}) as Record<string, any>;
              const projectedCTSeconds = Number((simOverride as any)?.timeOverrideSeconds ?? (simOverride as any)?.timeOverride ?? kaizenMetrics.cycleTime?.projectedSeconds ?? (data as any).vsmKaizenProjection?.projectedSeconds ?? 0) || 0;
              const hasProjectedCTChange = projectedCTSeconds > 0 && Math.abs(projectedCTSeconds - ctSeconds) > 0.0001;
              const projectedCTDisplay = hasProjectedCTChange
                ? vsmCTUnit === 'min'
                  ? `${(projectedCTSeconds / 60).toFixed(2).replace(/\.00$/, '')}min`
                  : `${Math.round(projectedCTSeconds)}s`
                : '';
              const projectedSetup = kaizenMetrics.setup;
              const setupCurrentValue = Number(projectedSetup?.currentValue ?? vsmCO ?? 0);
              const setupProjectedValue = Number(projectedSetup?.projectedValue ?? 0);
              const hasProjectedSetupChange = Boolean(projectedSetup) && Number.isFinite(setupProjectedValue) && Math.abs(setupProjectedValue - setupCurrentValue) > 0.0001;
              const projectedSetupDisplay = hasProjectedSetupChange
                ? `${setupProjectedValue.toFixed(2).replace(/\.00$/, '')} min`
                : '';
              const projectedAvailability = kaizenMetrics.availability;
              const availabilityCurrentValue = Number(projectedAvailability?.currentValue ?? vsmDisp ?? 0);
              const availabilityProjectedValue = Number(projectedAvailability?.projectedValue ?? 0);
              const hasProjectedAvailabilityChange = Boolean(projectedAvailability) && Number.isFinite(availabilityProjectedValue) && Math.abs(availabilityProjectedValue - availabilityCurrentValue) > 0.0001;
              const projectedAvailabilityDisplay = hasProjectedAvailabilityChange
                ? `${availabilityProjectedValue.toFixed(2).replace(/\.00$/, '')}%`
                : '';
              // V232 — Estado Futuro: troca o display por valor efetivo (projetado quando existe),
              // sem strikethrough/seta. Pega o valor via helper centralizado para garantir
              // consistência com LeadTimeLadder e demais consumidores.
              const isFuturoView = viewMode === 'future_simulated';
              const isKaizenView = viewMode === 'kaizen_work';
              const effectiveCTSec = isFuturoView ? getEffectiveMetric(data, 'cycleTime', viewMode) : 0;
              const effectiveCTDisplay = isFuturoView
                ? (vsmCTUnit === 'min'
                    ? `${(effectiveCTSec / 60).toFixed(2).replace(/\.00$/, '')}min`
                    : `${Math.round(effectiveCTSec)}s`)
                : '';
              const futuroCTChanged = isFuturoView && Math.abs(effectiveCTSec - ctSeconds) > 0.0001;
              const effectiveSetup = isFuturoView ? getEffectiveMetric(data, 'setup', viewMode) : 0;
              const effectiveSetupDisplay = isFuturoView
                ? `${effectiveSetup.toFixed(2).replace(/\.00$/, '')}`
                : '';
              const futuroSetupChanged = isFuturoView && Math.abs(effectiveSetup - (Number(vsmCO) || 0)) > 0.0001;
              const effectiveDisp = isFuturoView ? getEffectiveMetric(data, 'availability', viewMode) : 0;
              const effectiveDispDisplay = isFuturoView
                ? `${effectiveDisp.toFixed(2).replace(/\.00$/, '')}%`
                : '';
              const futuroDispChanged = isFuturoView && Math.abs(effectiveDisp - (Number(vsmDisp) || 0)) > 0.0001;
              const futuroValueColor = '#67e8f9';
              const taktSeconds = taktTime * 60;
              const isAndon = ctSeconds > 0 && taktSeconds > 0 && ctSeconds > taktSeconds;
              const processoDataBoxSrc = isBlueprintVsm
                ? isCellProcess ? '/assets/vsm/blueprint/celula.svg' : '/assets/vsm/blueprint/processo-databox.svg'
                : isSketchVsm
                  ? isCellProcess ? '/assets/vsm/sketch/celula.svg' : '/assets/vsm/sketch/processo-databox.svg'
                  : isCellProcess ? '/assets/vsm/sketch/celula.svg' : '/assets/vsm/processo-databox.svg';
              const userVsmColor = vsmCardColor || (isDark ? '#7dd3fc' : '#1e3a5f');
              const labelColor = isBlueprintVsm ? userVsmColor : isTechnicalVsm ? techInk : (isDark ? '#94a3b8' : '#222');
              const valueColor = isBlueprintVsm ? (isDark ? '#dbeafe' : '#0f172a') : isTechnicalVsm ? techInk : (isDark ? '#f1f5f9' : '#000');
              const titleColor = isBlueprintVsm ? (isDark ? '#dbeafe' : '#0f172a') : isTechnicalVsm ? techInk : (isDark ? '#f1f5f9' : '#000');
              const sketchSelectionFilter = [
                'drop-shadow(1px 0 0 #2563eb)',
                'drop-shadow(-1px 0 0 #2563eb)',
                'drop-shadow(0 1px 0 #2563eb)',
                'drop-shadow(0 -1px 0 #2563eb)',
                'drop-shadow(1px 1px 0 #2563eb)',
                'drop-shadow(-1px -1px 0 #2563eb)',
                'drop-shadow(1px -1px 0 #2563eb)',
                'drop-shadow(-1px 1px 0 #2563eb)',
              ].join(' ');
              const classicSelectedGlow = 'drop-shadow(0 0 0px #2563eb) drop-shadow(0 0 1px #2563eb) drop-shadow(0 0 2px #2563eb)';
              const imageFilter = isBlueprintVsm
                ? 'none'
                : isSketchVsm
                  ? (selected ? sketchSelectionFilter : 'none')
                  : (selected
                      ? (isDark ? `invert(1) ${classicSelectedGlow}` : classicSelectedGlow)
                      : (isDark ? 'invert(1)' : 'none'));
              const imageBlendMode: React.CSSProperties['mixBlendMode'] = !isBlueprintVsm && isDark ? 'screen' : 'normal';
              const blueprintGlow = selected
                ? `drop-shadow(0 0 2px ${userVsmColor}) drop-shadow(0 0 7px ${userVsmColor}cc) drop-shadow(0 0 14px ${userVsmColor}66)`
                : 'none';
              return (
                <div style={{
                  position: 'relative', width: pdWidth, height: totalH, pointerEvents: 'none', flexShrink: 0,
                  backgroundColor: 'transparent',
                  backdropFilter: undefined,
                  WebkitBackdropFilter: undefined,
                  border: undefined,
                  borderRadius: undefined,
                  isolation: 'isolate',
                  filter: isBlueprintVsm ? blueprintGlow : undefined,
                  ...(isAndon ? { outline: '2px solid #ef4444', boxShadow: '0 0 10px rgba(239,68,68,0.3)' } : {}),
                }}>
                  {isVectorVsm ? (
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        color: isTechnicalVsm ? techInk : userVsmColor,
                      }}
                    >
                      {isCellProcess ? (
                        <BpCelula
                          width={pdWidth}
                          height={totalH}
                          color={isTechnicalVsm ? techInk : userVsmColor}
                          useGradient={!isTechnicalVsm}
                          sketchMode={false}
                          fillColor={isTechnicalVsm ? (vsmCardColor || (isDark ? '#cbd5e1' : '#e5e7eb')) : undefined}
                          fillOpacityBoost={isTechnicalVsm ? 12.5 : undefined}
                        />
                      ) : (
                        <BpDatabox
                          width={pdWidth}
                          height={totalH}
                          color={isTechnicalVsm ? techInk : userVsmColor}
                          useGradient={!isTechnicalVsm}
                          sketchMode={false}
                          fillColor={isTechnicalVsm ? (vsmCardColor || (isDark ? '#cbd5e1' : '#e5e7eb')) : undefined}
                          fillOpacityBoost={isTechnicalVsm ? 12.5 : undefined}
                        />
                      )}
                    </div>
                  ) : (
                    <img
                      src={processoDataBoxSrc}
                      alt=""
                      draggable={false}
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill', pointerEvents: 'none', userSelect: 'none', filter: imageFilter, mixBlendMode: imageBlendMode }}
                    />
                  )}
                  {/* Process name in top bar — larger centered font */}
                  <div style={{ position: 'absolute', top: isCellProcess ? '1.4%' : isVectorVsm ? '1.4%' : '1.4%', left: 5, right: 5, height: isCellProcess ? '16.0%' : isVectorVsm ? '17.5%' : '13.5%', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'auto', overflow: 'hidden' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: titleColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', textAlign: 'center', fontFamily: overlayFontFamily }}>
                      {effectiveAtividade || ''}
                    </span>
                  </div>
                  {/* Operator icon + count — bottom right of process body */}
                  {vsmOp && Number(vsmOp) > 0 ? (
                    <div style={{ position: 'absolute', right: operatorInset, bottom: isCellProcess ? totalH - processBodyHeight + 28 : totalH - processBodyHeight + operatorInset, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 1, pointerEvents: 'none' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: valueColor }}>{vsmOp}</span>
                      <img src="/assets/vsm/operador.svg" alt="op" style={{ width: 17, height: 17, objectFit: 'contain', filter: isDark ? 'invert(1)' : 'none', mixBlendMode: isDark ? 'screen' : 'normal' }} draggable={false} />
                    </div>
                  ) : null}
                  {/* DataBox row overlays */}
                  <div style={rowStyle(isCellProcess ? '58.3%' : isVectorVsm ? '58.7%' : '56.3%')}>
                    <span style={{ flex: 1, textAlign: 'center', fontWeight: 700, color: valueColor }}>
                      {vsmPerfil === 'Compartilhado' ? 'Compartilhado' : 'Dedicado'}
                    </span>
                  </div>
                  {hasVsmCT && (
                    <div style={rowStyle(isCellProcess ? '70.0%' : isVectorVsm ? '70.5%' : '68.4%')}>
                      <span style={{ flex: '0 0 40%', paddingLeft: 10, color: labelColor }}>T/C</span>
                      <span style={{ flex: 1, paddingRight: 10, textAlign: 'right', fontWeight: 700, color: valueColor }}>
                        {isKaizenView && projectedCTDisplay ? (
                          <>
                            <span style={{ textDecoration: 'line-through', opacity: 0.62 }}>{vsmCT}{vsmCTUnit}</span>
                            <span style={{ color: futuroValueColor, marginLeft: 4 }}>→ {projectedCTDisplay}</span>
                          </>
                        ) : isFuturoView ? (
                          <span style={{ color: futuroCTChanged ? futuroValueColor : valueColor }}>{effectiveCTDisplay}</span>
                        ) : `${vsmCT}${vsmCTUnit}`}
                      </span>
                    </div>
                  )}
                  {hasVsmCO && (
                    <div style={rowStyle(isCellProcess ? '81.6%' : isVectorVsm ? '82.3%' : '80.4%')}>
                      <span style={{ flex: '0 0 40%', paddingLeft: 10, color: labelColor }}>Setup</span>
                      <span style={{ flex: 1, paddingRight: 10, textAlign: 'right', fontWeight: 700, color: valueColor }}>
                        {isKaizenView && projectedSetupDisplay ? (
                          <>
                            <span style={{ textDecoration: 'line-through', opacity: 0.62 }}>{setupCurrentValue.toFixed(2).replace(/\.00$/, '')} min</span>
                            <span style={{ color: futuroValueColor, marginLeft: 4 }}>→ {projectedSetupDisplay}</span>
                          </>
                        ) : isFuturoView ? (
                          <span style={{ color: futuroSetupChanged ? futuroValueColor : valueColor }}>{effectiveSetupDisplay}</span>
                        ) : `${vsmCO}`}
                      </span>
                    </div>
                  )}
                  {hasVsmDisp && (
                    <div style={rowStyle(isCellProcess ? '93.4%' : isVectorVsm ? '94.1%' : '92.5%')}>
                      <span style={{ flex: '0 0 40%', paddingLeft: 10, color: labelColor }}>Disp</span>
                      <span style={{ flex: 1, paddingRight: 10, textAlign: 'right', fontWeight: 700, color: valueColor }}>
                        {isKaizenView && projectedAvailabilityDisplay ? (
                          <>
                            <span style={{ textDecoration: 'line-through', opacity: 0.62 }}>{availabilityCurrentValue.toFixed(2).replace(/\.00$/, '')}%</span>
                            <span style={{ color: futuroValueColor, marginLeft: 4 }}>→ {projectedAvailabilityDisplay}</span>
                          </>
                        ) : isFuturoView ? (
                          <span style={{ color: futuroDispChanged ? futuroValueColor : valueColor }}>{effectiveDispDisplay}</span>
                        ) : `${vsmDisp}%`}
                      </span>
                    </div>
                  )}
                </div>
              );
            }

            // V227 — Quando entity (fornecedor/cliente) tem databox acoplado, força a mesma
            // largura do processo+databox (121px) e renderiza o card via SVG inline com strokes
            // finos (1.6, idênticos ao processo-databox.svg) em vez do externo.svg silhuetado.
            const isEntityWithDatabox = isExternal && hasExternalData;
            const entityFixedWidth = 121;
            const entityFixedHeight = 90;
            const containerWidth = isEntityWithDatabox ? entityFixedWidth : cardWidth;
            const entityStrokeColor = isBlueprintVsm ? (vsmCardColor || '#06b6d4') : isTechnicalVsm ? techInk : '#111827';
            const entityFillColor = vsmCardColor || (isDark ? '#7dd3fc' : '#7dd3fc');

            return (
              /* VSM-specific shape: overlaid text + optional DataBox below */
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none', width: containerWidth, justifyContent: 'flex-start', background: 'transparent' }}>

                {/* Main shape with inline overlays */}
                <div style={{ position: 'relative', width: '100%', height: isExternal ? entityFixedHeight : cardHeight, backgroundColor: 'transparent' }}>
                  {/* Nivelamento: guia de alinhamento central (visível quando selecionado) */}
                  {isNivelamento && selected && (
                    <div style={{
                      position: 'absolute', left: 0, right: 0,
                      top: '50%', transform: 'translateY(-50%)',
                      height: 0, borderTop: '1px dashed #06b6d4',
                      opacity: 0.6, pointerEvents: 'none', zIndex: 10,
                    }} />
                  )}
                  <div style={isDockedTransport ? { transform: 'translateY(25px) rotate(90deg)', transformOrigin: 'center center', width: 110, height: 60 } : undefined}>
                  {isEntityWithDatabox ? (
                    // Entity inline SVG: factory shape com stroke fino igual ao processo.
                    // Telhado estilo "shed roof" (saw-tooth assimétrico) com 4 dentes inclinados
                    // (riser vertical à esquerda, slope diagonal à direita) + anexo flat-top à direita
                    // simulando a chaminé/galpão técnico, conforme padrão clássico de fábrica.
                    <svg
                      width={entityFixedWidth}
                      height={entityFixedHeight}
                      viewBox="0 0 130 90"
                      preserveAspectRatio="none"
                      style={{ display: 'block' }}
                    >
                      <path
                        d="M 0 22 L 0 2 L 22 22 L 22 2 L 44 22 L 44 2 L 66 22 L 66 2 L 88 22 L 88 2 L 130 2 L 130 90 L 0 90 Z"
                        fill={entityFillColor}
                        stroke={entityStrokeColor}
                        strokeWidth="1.6"
                        strokeLinejoin="miter"
                        strokeLinecap="square"
                        vectorEffect="non-scaling-stroke"
                      />
                    </svg>
                  ) : (
                  // @ts-ignore
                  <VsmCanvasShape
                    vsmIcon={data.vsmIcon}
                    width={isDockedTransport ? 110 : cardWidth}
                    height={isDockedTransport ? 60 : isExternal ? 90 : cardHeight}
                    selected={selected}
                    style={(isDockedTransport ? { transform: 'rotate(-90deg)', transformOrigin: 'center center' } : undefined) as any}
                  />
                  )}
                  </div>

                  {isDockedTransport && (
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 110,
                        width: 110,
                        minHeight: 18,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'transparent',
                        pointerEvents: 'auto',
                      }}
                    >
                      {renderEditableTitle(
                        (data as any).vsmFrequencia || data.atividade || 'Diária',
                        {
                          fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, monospace',
                          fontSize: 11,
                          lineHeight: 1.1,
                          fontWeight: 700,
                          color: themeStrokeColor,
                          whiteSpace: 'pre-line',
                          textAlign: 'center',
                        },
                        'w-full text-center'
                      )}
                    </div>
                  )}

                  {/* PROCESSO: name in top bar (~22%), operator icon + count in bottom right */}
                  {isProcesso && (
                    <>
                      <div style={{ position: 'absolute', top: isVectorVsm ? '2.6%' : 0, left: 4, right: 4, height: isVectorVsm ? '33.1%' : '22%', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'auto', overflow: 'hidden' }}>                        <span style={{ fontSize: 12, fontWeight: 700, color: themeStrokeColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                          {effectiveVsmTitle || ''}
                        </span>
                      </div>
                      {vsmOp && Number(vsmOp) > 0 ? (
                        <div style={{ position: 'absolute', right: 7, bottom: 7, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 1, pointerEvents: 'none' }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: themeStrokeColor }}>{vsmOp}</span>
                          <img src="/assets/vsm/operador.svg" alt="op" style={{ width: 14, height: 14, objectFit: 'contain' }} draggable={false} />
                        </div>
                      ) : null}
                    </>
                  )}

                  {/* ESTOQUE: qty + unit inside the triangle, always rendered to suppress label */}
                  {isEstoque && (
                    <div style={{ position: 'absolute', top: '59%', left: 0, right: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', transform: 'translateY(-50%)', pointerEvents: 'none', gap: 1 }}>
                      {showInventoryCoverage ? (
                        <>
                          <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#e2e8f0' : '#111827' }}>{vsmDias}</span>
                          <span style={{ fontSize: 9, color: isDark ? '#94a3b8' : '#555' }}>Dias</span>
                        </>
                      ) : (
                        <>
                          {inventory ? <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#e2e8f0' : '#111827' }}>{inventory}</span> : null}
                          {vsmUnit ? <span style={{ fontSize: 9, color: isDark ? '#94a3b8' : '#555' }}>{vsmUnit}</span> : null}
                        </>
                      )}
                    </div>
                  )}

                  {/* EXTERNO (fornecedor/cliente): nome com word-wrap 2 linhas — V104.0 */}
                  {isExternal && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'auto', padding: '20px 8px 6px 8px', textAlign: 'center' }}>
                      <span style={{
                        fontSize: 12, fontWeight: 700, color: themeStrokeColor,
                        maxWidth: '100%', lineHeight: 1.2,
                        wordBreak: 'break-word', whiteSpace: 'pre-wrap',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      } as React.CSSProperties}>
                        {vsmNome || effectiveAtividade || ''}
                      </span>
                    </div>
                  )}

                  {/* ENTREGA: frequência renderizada abaixo da forma, não dentro */}

                  {/* OTHER VSM ICONS (not factory-specific): label below shape */}
                </div>

                {/* V104.0 — Data box dinâmica: produto/frequência/mix/demanda */}
                {isExternal && hasExternalData && (() => {
                  const activeMix = vsmDemandMix.filter(v => v.qty > 0);
                  const mainProduct = activeMix[0];
                  const variants = activeMix;
                  const borderColor = isBlueprintVsm ? (vsmCardColor || '#06b6d4') : isTechnicalVsm ? techInk : '#111827';
                  const textColor = isDark ? '#f8fafc' : '#111827';
                  const mutedColor = isDark ? '#94a3b8' : '#6b7280';
                  const rowFontFamily = 'Inter, "Segoe UI", system-ui, sans-serif';
                  const ROW_H = 20;
                  const ROW_FS = 10;
                  const rowBase: React.CSSProperties = {
                    height: ROW_H,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 6px',
                    fontSize: ROW_FS,
                    lineHeight: 1,
                    fontFamily: rowFontFamily,
                    color: textColor,
                  };
                  return (
                    <div style={{
                      width: '100%',
                      // Overlap mínimo para fundir a borda inferior do entity (stroke 1.6 ≈ 1.5px)
                      // com a borda superior do databox numa única linha contínua.
                      marginTop: -1.5,
                      border: `1.5px solid ${borderColor}`,
                      background: canvasBg,
                      pointerEvents: 'auto',
                    }}>
                      {/* Produto Principal */}
                      {(vsmProduto || mainProduct) && (
                        <div style={{
                          ...rowBase,
                          justifyContent: 'center',
                          fontWeight: 700,
                          borderBottom: `1px solid ${borderColor}`,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {vsmProduto || mainProduct?.label || ''}
                        </div>
                      )}
                      {/* Frequência de Entrega */}
                      {vsmFrequencia && (
                        <div style={{
                          ...rowBase,
                          justifyContent: 'center',
                          fontWeight: 500,
                          borderBottom: `1px solid ${borderColor}`,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {vsmFrequencia}
                        </div>
                      )}
                      {/* Variantes do mix */}
                      {variants.map((v, i) => (
                        <div key={i} style={{
                          ...rowBase,
                          justifyContent: 'space-between',
                          borderBottom: (i < variants.length - 1 || vsmDemanda || (data as any).vsmDeliveryLot > 0) ? `1px solid ${borderColor}` : 'none',
                        }}>
                          <span style={{ color: mutedColor, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '40%' }}>{v.label}</span>
                          <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{`${v.qty.toLocaleString('pt-BR')} pç/mês`}</span>
                        </div>
                      ))}
                      {/* Demanda diária */}
                      {vsmDemanda && (
                        <div style={{
                          ...rowBase,
                          justifyContent: 'center',
                          fontWeight: 700,
                          borderBottom: (data as any).vsmDeliveryLot > 0 ? `1px solid ${borderColor}` : 'none',
                          whiteSpace: 'nowrap', overflow: 'hidden',
                        }}>
                          {`${Number(vsmDemanda).toLocaleString('pt-BR')} pç/dia`}
                        </div>
                      )}
                      {/* Lote de Entrega — LE */}
                      {(data as any).vsmDeliveryLot > 0 && (
                        <div style={{
                          ...rowBase,
                          justifyContent: 'space-between',
                          whiteSpace: 'nowrap', overflow: 'hidden',
                        }}>
                          <span style={{ color: mutedColor, fontWeight: 600 }}>LE</span>
                          <span style={{ fontWeight: 700 }}>{`${Number((data as any).vsmDeliveryLot).toLocaleString('pt-BR')} pç`}</span>
                        </div>
                      )}
                      {/* Fallback: linhas legacy (vsmLinha4/5) sem mix novo */}
                      {activeMix.length === 0 && [(data as any).vsmLinha4, (data as any).vsmLinha5].filter(Boolean).map((line: string, i: number) => (
                        <div key={`extra-${i}`} style={{
                          ...rowBase,
                          justifyContent: 'center',
                          fontWeight: 500,
                          borderTop: `1px solid ${borderColor}`,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {line}
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* Label below shape for non-processo non-external non-estoque */}
                {!isProcesso && !isExternal && !isEstoque && !isProduto && !isTransport && !isPcpm && !isKanbanPosto && data.vsmIcon !== 'nivelamento' && (
                  <div style={{ pointerEvents: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', marginTop: 2, padding: '1px 2px' }}>
                    {renderEditableTitle(
                      effectiveAtividade || data.vsmIcon,
                      { fontSize: '10px', color: classicSmartText, fontWeight: 600 },
                      "flex items-center justify-center text-center w-full"
                    )}
                    {isEntrega && vsmFrequencia && (
                      <span style={{ fontSize: 9, color: themeStrokeColor, fontWeight: 500, marginTop: 1 }}>{vsmFrequencia}</span>
                    )}
                  </div>
                )}

              </div>
            );
          })() : (isInitial || isFinal) ? (
            <div style={{ pointerEvents: 'auto' }}>
              <ClassicExternalSource
                width={220}
                height={cardHeight}
                userBg={classicBg}
                strokeColor={themeStrokeColor}
              />
            </div>
          ) : (data as any).mapOrigin === 'FACTORY' ? (
            <FactoryProcessShape
              width={cardWidth}
              height={cardHeight}
              selected={selected}
              strokeColor={themeStrokeColor}
              fillColor={classicBg || themeCanvasBg}
            />
          ) : (
            <ClassicProcessShape
              width={cardWidth}
              height={cardHeight}
              userBg={classicBg}
              selected={selected}
              strokeColor={themeStrokeColor}
            />
          )}
          {/* Textos dentro das figuras */}
          {isInitial && !data.vsmIcon && (
            <div
              className="absolute top-0 left-0 right-0 bottom-0 flex flex-col items-center justify-center text-center px-2"
              style={{ pointerEvents: 'auto', gap: '4px' }}
            >
              <div style={{ fontSize: '13px', color: classicSmartText, fontWeight: 700 }}>
                Início
              </div>
              <div style={{ fontSize: '10px', color: classicSmartText, fontWeight: 400 }}>
                {data.cliente || 'Cliente Interno'}
              </div>
              <div style={{ fontSize: '18px', color: classicSmartText, fontWeight: 700 }}>
                {data.entrada ?? 0} <span style={{ fontSize: '10px', fontWeight: 400 }}>{(data as any).unidadeDemanda || '/dia'}</span>
              </div>
            </div>
          )}
          {isFinal && !data.vsmIcon && (
            <div
              className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center text-center px-2"
              style={{ fontSize: '18px', color: classicSmartText, fontWeight: 700 }}
            >
              {effectiveNomeFluxo || 'Fim'}
            </div>
          )}
          {!isInitial && !isFinal && !data.vsmIcon && (
            <>
              {/* ðŸÂÂ¢ ENTERPRISE UI: Header Integrado (ID + Ocupação) - Classic Layout */}
              {/* Posicionado dentro da primeira célula do SVG hand-drawn */}
              <div
                className="absolute left-0 right-0 flex items-center justify-between"
                style={{
                  top: '12px',
                  height: '14px',
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  pointerEvents: 'none',
                  zIndex: 5,
                }}
              >
                {/* Setor (Esquerda) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden', maxWidth: '40%' }}>
                  {sectorName && (
                    <span style={{ fontSize: '9px', fontWeight: 400, letterSpacing: '0.05em', lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.75, color: classicSmartText, userSelect: 'none' }} title={sectorName}>{sectorName.toUpperCase()}</span>
                  )}
                </div>
                {/* Centro: nmero em destaque */}
                {data.numeroSequencial !== undefined && data.numeroSequencial !== null && (
                  <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', fontSize: '9px', fontWeight: 400, fontFamily: "'Roboto Mono', monospace", letterSpacing: '0.05em', lineHeight: 1, opacity: 0.9, color: classicSmartText, userSelect: 'none', whiteSpace: 'nowrap' }}>
                    #{String(data.numeroSequencial).padStart(3, '0')}
                  </span>
                )}
                {/* % (Direita) */}
                {showPercentBadge && (() => {
                  const occValue = loadValue;
                  const isCritical = occValue > 100;
                  const isWarning = occValue > 85;
                  const val = heatmapMode && heatmapLevel ? Math.round(heatmapLevel.value) : occValue;
                  const isCrit2 = heatmapMode && heatmapLevel ? val > 100 : isCritical;
                  const isWarn2 = heatmapMode && heatmapLevel ? val > 85 : isWarning;
                  return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '9px', fontWeight: isCrit2 ? 600 : 400, color: isCrit2 ? '#991b1b' : classicSmartText, backgroundColor: isCrit2 ? '#fee2e2' : 'transparent', borderRadius: isCrit2 ? '10px' : undefined, padding: isCrit2 ? '1px 5px' : '0', lineHeight: 1, userSelect: 'none', flexShrink: 0 }}>
                      {!isCrit2 && <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: isWarn2 ? '#f59e0b' : '#22c55e', display: 'inline-block' }} />}
                      {heatmapMode && heatmapLevel ? `${val}%` : `${occValue.toFixed(1)}%`}
                    </span>
                  );
                })()}
              </div>

              {/* Nome da atividade - deslocado para baixo para acomodar header */}
              {renderEditableTitle(
                effectiveAtividade || 'Nome da Atividade',
                { fontSize: '11px', color: classicSmartText, fontWeight: 500 },
                mapType === 'FACTORY' 
                  ? "absolute left-0 right-0 flex items-center justify-center text-center px-2 top-[26px] h-[34px]"
                  : "absolute left-0 right-0 flex items-center justify-center text-center px-2 top-[24px] h-[36px]"
              )}
              
              {mapType === 'FACTORY' ? (
                <>
                  {/* Campos fixos para FACTORY: T/C, T/R, OEE, Lote */}
                  {/* Linha de T/C e T/R */}
                  <div 
                    className="absolute left-0 right-0 flex flex-row border-t border-black"
                    style={{ top: '60px', height: '55px' }}
                  >
                    <div className="flex-1 flex flex-col items-center justify-center border-r border-black p-1">
                      <span className="text-[9px] uppercase font-bold text-gray-500">T/C</span>
                      <span className="text-[11px] font-bold">{ltInMinutes > 0 ? formatTimeDisplay(ltInMinutes, customUnitLT) : '-'}</span>
                    </div>
                    <div className="flex-1 flex flex-col items-center justify-center p-1">
                      <span className="text-[9px] uppercase font-bold text-gray-500">T/R</span>
                      <span className="text-[11px] font-bold">{ttInMinutes > 0 ? formatTimeDisplay(ttInMinutes, customUnitTT) : '-'}</span>
                    </div>
                  </div>
                  {/* Linha de OEE e Lote */}
                  <div 
                    className="absolute left-0 right-0 flex flex-row border-t border-black"
                    style={{ top: '115px', height: '45px' }}
                  >
                    <div className="flex-1 flex flex-col items-center justify-center border-r border-black p-1">
                      <span className="text-[9px] uppercase font-bold text-gray-500">Eficácia</span>
                      <span className="text-[11px] font-bold font-mono">{data.oee ? `${data.oee}%` : '-'}</span>
                    </div>
                    <div className="flex-1 flex flex-col items-center justify-center p-1">
                      <span className="text-[9px] uppercase font-bold text-gray-500">Lote</span>
                      <span className="text-[11px] font-bold">{data.batchSize || '-'}</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* QUEM row */}
                  <div
                    className="absolute left-0 right-0 flex items-center"
                    style={{ top: '68px', height: '34px', paddingLeft: '8px', paddingRight: '6px', gap: '6px' }}
                  >
                    <span style={{ fontSize: '8px', fontWeight: 700, textTransform: 'uppercase' as const, color: classicSmartTextDim, letterSpacing: '0.06em', flexShrink: 0, minWidth: '32px' }}>QUEM</span>
                    <span style={{ fontSize: '11px', fontWeight: 500, color: classicSmartText, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const, flex: 1 }}>
                      {formatResourcesDisplay((liveCard ?? data) as any, organization) || '-'}
                    </span>
                  </div>
                  {/* COMO row */}
                  <div
                    className="absolute left-0 right-0 flex items-center"
                    style={{ top: '102px', height: '34px', paddingLeft: '8px', paddingRight: '6px', gap: '6px' }}
                  >
                    <span style={{ fontSize: '8px', fontWeight: 700, textTransform: 'uppercase' as const, color: classicSmartTextDim, letterSpacing: '0.06em', flexShrink: 0, minWidth: '32px' }}>COMO</span>
                    <span style={{ fontSize: '11px', fontWeight: 500, color: classicSmartText, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const, flex: 1 }}>
                      {effectiveSistema || '-'}
                    </span>
                  </div>
                  {/* P / TP / TRA row */}
                  <div
                    className="absolute left-0 right-0 flex flex-row"
                    style={{ top: '136px', height: '38px' }}
                  >
                    <div className="flex-1 flex flex-col items-center justify-center">
                      <span style={{ fontSize: '7px', fontWeight: 700, textTransform: 'uppercase' as const, color: classicSmartTextDim, letterSpacing: '0.06em' }}>P</span>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: isSimulatedPChanged ? '#2563eb' : classicSmartText }}>
                        {effectivePValue > 0 ? formatNumber(effectivePValue, 2) : '-'}
                      </span>
                    </div>
                    <div className="flex-1 flex flex-col items-center justify-center">
                      <span style={{ fontSize: '7px', fontWeight: 700, textTransform: 'uppercase' as const, color: classicSmartTextDim, letterSpacing: '0.06em' }}>TP</span>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: classicSmartText }}>
                        {ltInMinutes > 0 ? formatTimeDisplay(ltInMinutes, customUnitLT) : '-'}
                      </span>
                    </div>
                    <div className="flex-1 flex flex-col items-center justify-center">
                      <span style={{ fontSize: '7px', fontWeight: 700, textTransform: 'uppercase' as const, color: classicSmartTextDim, letterSpacing: '0.06em' }}>TRA</span>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: classicSmartText }}>
                        {ttInMinutes > 0 ? formatTimeDisplay(ttInMinutes, customUnitTT) : '-'}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      ) : effectiveLayoutType === 'postit' ? (
        // LAYOUT POSTIT (incluindo card inicial)
        <div 
          className={`w-full flex flex-col overflow-hidden font-sans ${isInitial || isFinal ? 'rounded-full' : 'rounded'} relative border-2 ${simulationHeatmapClasses}`}
          style={{
            minHeight: cardHeight, // Garantir altura mínima de 160px
            backgroundColor: isSimCrisis ? undefined : (heatmapBg || (mapType === 'FACTORY' ? '#ffffff' : ((colorCardsBySector ? sectorColor : undefined) || ribbonColorBg || postItBgColor || postItTheme.bg))),
            borderColor: isSimOverloaded ? '#dc2626' : (selected ? '#3b82f6' : (postItBorderColor || userBorderColorHex)),
            color: mapType === 'FACTORY' ? '#000000' : postItSmartText,
            boxShadow: bottleneckGlow ?? (selected ? '0 0 0 1px #3b82f6, 0 0 8px rgba(59,130,246,0.35)' : 'none'),
            animation: bottleneckAnimation,
            borderRadius: isInitial || isFinal ? '9999px' : undefined,
            opacity: showGhostStyle ? 0.4 : 1,
            filter: showGhostStyle ? 'grayscale(100%)' : 'none',
            transition: simIsActive ? 'box-shadow 300ms ease, border-color 300ms ease, background-color 300ms ease' : undefined,
            outline: 'none',
          }}
        >
          {/* Badge row: setor  #ID (esq) | % (dir) */}
          {!isInitial && !isFinal && !data.vsmIcon && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 7px 0 7px', height: '14px', flexShrink: 0, pointerEvents: 'none', position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden', maxWidth: '40%' }}>
                {sectorName && (
                  <span style={{ fontSize: '9px', fontWeight: 400, letterSpacing: '0.05em', lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.75, color: postItSmartText, userSelect: 'none' }} title={sectorName}>{sectorName.toUpperCase()}</span>
                )}
              </div>
              {/* Centro: nmero em destaque */}
              {data.numeroSequencial !== undefined && data.numeroSequencial !== null && (
                <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', fontSize: '9px', fontWeight: 400, fontFamily: "'Roboto Mono', monospace", letterSpacing: '0.05em', lineHeight: 1, opacity: 0.9, color: postItSmartText, userSelect: 'none', whiteSpace: 'nowrap' }}>
                  #{String(data.numeroSequencial).padStart(3, '0')}
                </span>
              )}
              {showPercentBadge && (() => {
                const occValue = loadValue;
                const isCritical = occValue > 100;
                const isWarning = occValue > 85;
                const val = heatmapMode && heatmapLevel ? Math.round(heatmapLevel.value) : occValue;
                const isCrit2 = heatmapMode && heatmapLevel ? val > 100 : isCritical;
                const isWarn2 = heatmapMode && heatmapLevel ? val > 85 : isWarning;
                return (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '9px', fontWeight: isCrit2 ? 600 : 400, color: isCrit2 ? '#991b1b' : postItSmartText, backgroundColor: isCrit2 ? '#fee2e2' : 'transparent', borderRadius: isCrit2 ? '10px' : undefined, padding: isCrit2 ? '1px 5px' : '0', lineHeight: 1, userSelect: 'none', flexShrink: 0 }}>
                    {!isCrit2 && <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: isWarn2 ? '#f59e0b' : '#22c55e', display: 'inline-block' }} />}
                    {heatmapMode && heatmapLevel ? `${val}%` : `${occValue.toFixed(1)}%`}
                  </span>
                );
              })()}
            </div>
          )}
          {/* Ttulo do card postit */}
          {!isInitial && !isFinal && !data.vsmIcon && (
            <div className="flex-1 flex items-center justify-center px-3" style={{ padding: '6px 12px 2px 12px' }}>
                {renderEditableTitle(
                  effectiveAtividade || 'Nova Atividade',
                  {
                    color: postItSmartText,
                    fontSize: (() => {
                      if (userFontSize) return `${userFontSize}px`;
                      const len = (effectiveAtividade || 'Nova Atividade').length;
                      if (len <= 14) return '22px';
                      if (len <= 28) return '18px';
                      if (len <= 45) return '15px';
                      if (len <= 60) return '13px';
                      return '11px';
                    })(),
                    lineHeight: 1.25,
                    wordBreak: 'break-word',
                    textAlign: 'center',
                  },
                  "leading-tight w-full text-center"
                )}
            </div>
          )}
          {/* Conteúdo do card inicial no modo postit */}
          {isInitial && (
            <div className="flex-1 flex flex-col items-center justify-center p-4 text-center rounded-full" style={{ minHeight: 0 }}>
              <div 
                className="font-bold text-base leading-tight mb-2 w-full" 
                style={{ color: postItSmartText }}
              >
                Início
              </div>
              <div 
                className="text-xs mb-3 w-full" 
                style={{ 
                  color: postItSmartTextDim
                }}
              >
                {data.cliente || 'Cliente Interno'}
              </div>
              <div 
                className="text-2xl font-bold w-full" 
                style={{ 
                  color: postItSmartText
                }}
              >
                {data.entrada ?? 0} <span className="text-xs font-normal">{(data as any).unidadeDemanda || '/dia'}</span>
              </div>
            </div>
          )}
          {/* Conteúdo do card final no modo postit */}
          {isFinal && (
            <div className="flex-1 flex items-center justify-center p-4 text-center rounded-full" style={{ minHeight: 0 }}>
              <div 
                className="font-bold text-xl leading-tight w-full" 
                style={{ color: postItSmartText }}
              >
                {effectiveAtividade || 'Fim'}
              </div>
            </div>
          )}
          {/* Conteúdo de atividade normal no modo postit */}
          {!isInitial && !isFinal && !data.vsmIcon && (
            <>
              {/* Quem */}
              <div 
                className="px-2 py-2 border-t border-b flex flex-row items-center gap-2"
                style={{ borderColor: postItBorderColor || postItTheme.border }}
              >
                <span 
                  className="text-[10px] font-bold uppercase flex-shrink-0 leading-none" 
                  style={{ color: postItSmartTextDim, minWidth: '48px' }}
                >QUEM:</span>
                <span 
                  className="text-xs font-medium flex-1 text-left leading-none" 
                  style={{ color: postItSmartText }}
                >{formatResourcesDisplay(data, organization)}</span>
              </div>
              {/* Como */}
              <div 
                className="px-2 py-2 flex flex-row items-center gap-2"
              >
                <span 
                  className="text-[10px] font-bold uppercase flex-shrink-0 leading-none" 
                  style={{ color: postItSmartTextDim, minWidth: '48px' }}
                >COMO:</span>
                <span 
                  className="text-xs font-medium flex-1 text-left leading-none" 
                  style={{ color: postItSmartText }}
                >{effectiveSistema || '-'}</span>
              </div>
              {/* Métricas - Barra inferior */}
              <div 
                className="grid grid-cols-3 mt-auto rounded-b-xl"
                style={{ 
                  borderTopColor: postItBorderColor || postItTheme.border,
                  borderTopWidth: '1px',
                  borderTopStyle: 'solid',
                }}
              >
                <div className="flex flex-col items-center justify-center p-1" style={{ borderRightColor: postItBorderColor || postItTheme.border, borderRightWidth: '1px', borderRightStyle: 'solid' }}>
                  <span 
                    className="text-[9px] font-bold uppercase" 
                    style={{ color: postItSmartTextDim }}
                  >P</span>
                  <span 
                    className="text-sm font-bold" 
                    style={{ color: isSimulatedPChanged ? '#2563eb' : postItSmartText }}
                  >{effectivePValue > 0 ? formatNumber(effectivePValue, 2) : '0'}</span>
                </div>
                <div className="flex flex-col items-center justify-center p-1" style={{ borderRightColor: postItBorderColor || postItTheme.border, borderRightWidth: '1px', borderRightStyle: 'solid' }}>
                  <span 
                    className="text-[9px] font-bold uppercase" 
                    style={{ color: postItSmartTextDim }}
                  >TP</span>
                  <span
                    className="text-sm font-bold"
                    style={{ color: postItSmartText }}
                  >{ltInMinutes >= 0 ? formatTimeDisplay(ltInMinutes, customUnitLT) : '0'}</span>
                </div>
                <div className="flex flex-col items-center justify-center p-1">
                  <span
                    className="text-[9px] font-bold uppercase"
                    style={{ color: postItSmartTextDim }}
                  >TRA</span>
                  <span
                    className="text-sm font-bold"
                    style={{ color: postItSmartText }}
                  >{ttInMinutes >= 0 ? formatTimeDisplay(ttInMinutes, customUnitTT) : '0'}</span>
                </div>
              </div>
            </>
          )}
        </div>
      ) : effectiveLayoutType === 'minimal' ? (
        // LAYOUT MINIMAL - Design criativo e moderno (incluindo card inicial e final)
        <div 
          className={`w-full flex flex-col overflow-hidden border-2 ${isInitial || isFinal ? 'rounded-full' : 'rounded'} transition-all duration-300 ${simulationHeatmapClasses}`}
          style={(() => {
            // Usar layoutTheme calculado que já trata todas as cores corretamente
            const isWhiteColor = data.layoutColor === 'white';
            let borderColor = isWhiteColor ? '#000000' : (ribbonColorBg || (colorCardsBySector ? sectorColor : undefined) || layoutTheme?.accent || minimalTheme.accent || minimalTheme.border);
            const bgColor = isWhiteColor ? '#ffffff' : (borderColor || layoutTheme?.accent || minimalTheme.accent || minimalTheme.border);
            const linesUrl = frameLinesUrl;
            
            // Borda uniforme em todos os estados (selecionado ou não)
            if (selected && !isSimOverloaded) {
              return {
                backgroundColor: heatmapBg || sectorBg || bgColor,
                backgroundImage: linesUrl,
                backgroundSize: '10px 10px',
                border: `2px solid ${borderColor}`,
                boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.3)',
                boxSizing: 'border-box',
                minHeight: cardHeight,
                height: cardHeight,
                borderRadius: isInitial || isFinal ? '9999px' : undefined
              };
            }
            
            // Quando não selecionado, usar borda uniforme
            return {
              backgroundColor: isSimCrisis ? undefined : (heatmapBg || bgColor),
              border: isSimOverloaded ? '2px solid #dc2626' : `2px solid ${borderColor}`,
              boxSizing: 'border-box',
              backgroundImage: isSimCrisis ? undefined : linesUrl,
              backgroundSize: '10px 10px',
              boxShadow: bottleneckGlow,
              animation: bottleneckAnimation,
              transition: simIsActive ? 'box-shadow 300ms ease, border-color 300ms ease, background-color 300ms ease' : undefined,
              minHeight: cardHeight,
              height: cardHeight,
              borderRadius: isInitial || isFinal ? '9999px' : undefined
            };
          })()}
        >
          {/* Badge row: setor (esq) | #ID (centro) | % (dir) */}
          {!isInitial && !isFinal && !data.vsmIcon && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 7px 0 7px', height: '14px', flexShrink: 0, pointerEvents: 'none', position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden', maxWidth: '40%' }}>
                {sectorName && (
                  <span style={{ fontSize: '9px', fontWeight: 400, letterSpacing: '0.05em', lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.75, color: minimalTitleSmartText, userSelect: 'none' }} title={sectorName}>{sectorName.toUpperCase()}</span>
                )}
              </div>
              {data.numeroSequencial !== undefined && data.numeroSequencial !== null && (
                <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', fontSize: '9px', fontWeight: 400, fontFamily: "'Roboto Mono', monospace", letterSpacing: '0.05em', lineHeight: 1, opacity: 0.9, color: minimalTitleSmartText, userSelect: 'none', whiteSpace: 'nowrap' }}>
                  #{String(data.numeroSequencial).padStart(3, '0')}
                </span>
              )}
              {showPercentBadge && (() => {
                const occValue = loadValue;
                const isCritical = occValue > 100;
                const isWarning = occValue > 85;
                const val = heatmapMode && heatmapLevel ? Math.round(heatmapLevel.value) : occValue;
                const isCrit2 = heatmapMode && heatmapLevel ? val > 100 : isCritical;
                const isWarn2 = heatmapMode && heatmapLevel ? val > 85 : isWarning;
                return (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '9px', fontWeight: isCrit2 ? 600 : 400, color: isCrit2 ? '#991b1b' : minimalTitleSmartText, backgroundColor: isCrit2 ? '#fee2e2' : 'transparent', borderRadius: isCrit2 ? '10px' : undefined, padding: isCrit2 ? '1px 5px' : '0', lineHeight: 1, userSelect: 'none', flexShrink: 0 }}>
                    {!isCrit2 && <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: isWarn2 ? '#f59e0b' : '#22c55e', display: 'inline-block' }} />}
                    {heatmapMode && heatmapLevel ? `${val}%` : `${occValue.toFixed(1)}%`}
                  </span>
                );
              })()}
            </div>
          )}
          {/* Ttulo - rea colorida */}
          {!isInitial && !isFinal && !data.vsmIcon && (
            <div className="flex items-center justify-center px-3" style={(() => {
                const base = { marginTop: '5px', marginLeft: '6px', marginRight: '6px', height: '64px', overflow: 'hidden', borderRadius: '8px', backgroundImage: frameLinesUrl, backgroundSize: '10px 10px' };
                const isWhite = data.layoutColor === 'white';
                if (sectorColor) return { ...base, backgroundColor: sectorColor };
                if (isWhite) return { ...base, backgroundColor: '#f3f4f6', backgroundImage: undefined };
                if (layoutTheme && layoutTheme.accent) return { ...base, backgroundColor: ribbonColorBg || layoutTheme.accent };
                return { ...base, backgroundColor: ribbonColorBg || (userBg && effectiveLayoutType === 'minimal' ? userBg : (minimalTheme.accent || minimalTheme.border)) };
              })()}
            >
              {renderEditableTitle(
                isInitial ? 'Início' : isFinal ? 'Fim' : (effectiveAtividade || 'Nova Atividade'),
                {
                  color: minimalTitleSmartText,
                  fontSize: (() => {
                    if (userFontSize) return `${userFontSize}px`;
                    const len = (effectiveAtividade || 'Nova Atividade').length;
                    if (len <= 14) return '15px';
                    if (len <= 28) return '13px';
                    if (len <= 45) return '12px';
                    return '10px';
                  })(),
                  fontWeight: 700,
                  lineHeight: 1.25,
                  wordBreak: 'break-word' as const,
                  textAlign: 'center' as const,
                },
                "flex-1"
              )}
            </div>
          )}
          {/* Conteúdo do card inicial no modo minimal - Igual ao postit */}
          {isInitial && (
            <div className="flex-1 flex flex-col items-center justify-center p-4 text-center rounded-full" style={{ minHeight: 0, position: 'relative' }}>
              {frameLinesUrl && <div style={{ position: 'absolute', inset: 0, backgroundImage: frameLinesUrl, backgroundSize: '10px 10px', pointerEvents: 'none', borderRadius: '9999px' }} />}
              <div
                className="font-bold text-base leading-tight mb-2 w-full"
                style={{ color: minimalTitleSmartText }}
              >
                Início
              </div>
              <div 
                className="text-xs mb-3 w-full" 
                style={{ 
                  color: minimalAccentSmartTextDim
                }}
              >
                {data.cliente || 'Cliente Interno'}
              </div>
              <div 
                className="text-2xl font-bold w-full" 
                style={{ 
                  color: minimalTitleSmartText
                }}
              >
                {data.entrada ?? 0} <span className="text-xs font-normal">{(data as any).unidadeDemanda || '/dia'}</span>
              </div>
            </div>
          )}
          {/* Conteúdo do card final no modo minimal - Igual ao postit */}
          {isFinal && (
            <div className="flex-1 flex items-center justify-center p-4 text-center rounded-full" style={{ minHeight: 0, position: 'relative' }}>
              {frameLinesUrl && <div style={{ position: 'absolute', inset: 0, backgroundImage: frameLinesUrl, backgroundSize: '10px 10px', pointerEvents: 'none', borderRadius: '9999px' }} />}
              <div
                className="font-bold text-xl leading-tight w-full"
                style={{ color: minimalTitleSmartText }}
              >
                Fim
              </div>
            </div>
          )}
          {/* Conteúdo de atividade normal no modo minimal - Design otimizado UI/UX */}
          {!isInitial && !isFinal && !data.vsmIcon && (
            <div className="flex-1 flex flex-col" style={{ margin: '0 5px 5px 5px', padding: '6px 6px 8px 6px', justifyContent: 'center', gap: '5px', minHeight: 0, backgroundColor: data.layoutColor === 'white' ? '#f3f4f6' : '#ffffff', backgroundImage: bodyLinesUrl, backgroundSize: '8px 8px', borderRadius: '8px' }}>
              {/* Seção de InformaçÑÂµes - Layout Horizontal Compacto */}
              <div className="grid grid-cols-2" style={{ gap: '8px', flexShrink: 0, border: `1px solid ${ribbonColorBg || sectorColor || layoutTheme?.accent || minimalTheme.accent || minimalTheme.border}`, borderRadius: '8px', padding: '4px 8px' }}>
                <div className="flex flex-col" style={{ gap: '2px', minWidth: 0 }}>
                  <div style={{ 
                    fontSize: '8px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: data.layoutColor === 'white' ? '#9ca3af' : minimalBodySmartTextDim
                  }}>
                    QUEM
                  </div>
                  <div className="truncate" style={{ 
                    fontSize: '11px',
                    fontWeight: 500,
                    lineHeight: '1.2',
                    color: data.layoutColor === 'white' ? '#000000' : minimalBodySmartText
                  }}>
                    {formatResourcesDisplay(data, organization)}
                  </div>
                </div>
                <div className="flex flex-col" style={{ gap: '2px', minWidth: 0 }}>
                  <div style={{ 
                    fontSize: '8px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: data.layoutColor === 'white' ? '#9ca3af' : minimalBodySmartTextDim
                  }}>
                    COMO
                  </div>
                  <div className="truncate" style={{ 
                    fontSize: '11px',
                    fontWeight: 500,
                    lineHeight: '1.2',
                    color: data.layoutColor === 'white' ? '#000000' : minimalBodySmartText
                  }}>
                    {effectiveSistema || '-'}
                  </div>
                </div>
              </div>
              
              {/* Métricas em segmentos retangulares sólidos - Espaço otimizado para valores */}
              {(() => {
                const segmentBg = '#f3f4f6';
                const segmentText = minimalBodySmartText;
                
                const totalPeople = effectivePValue > 0 ? effectivePValue : 1;
                
                const tpValue = ltInMinutes > 0 ? formatTimeDisplay(ltInMinutes, customUnitLT) : '0';
                const traValue = ttInMinutes > 0 ? formatTimeDisplay(ttInMinutes, customUnitTT) : '0';
                
                return (
                  <div className="flex" style={{ height: '38px', width: '100%', flexShrink: 0, border: `1px solid ${ribbonColorBg || sectorColor || layoutTheme?.accent || minimalTheme.accent || minimalTheme.border}`, borderRadius: '10px', overflow: 'hidden' }}>
                    {/* Segmento P - Pessoas */}
                    <div 
                      className="flex-1 flex flex-col items-center justify-center"
                      style={{ 
                        backgroundColor: segmentBg,
                        color: segmentText,
                        borderRight: `1px solid ${ribbonColorBg || sectorColor || layoutTheme?.accent || minimalTheme.accent || minimalTheme.border}`,
                        borderBottomLeftRadius: '12px',
                        padding: '4px 2px',
                        minWidth: 0
                      }}
                    >
                      <div style={{ 
                        fontSize: '8px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        opacity: 0.7,
                        marginBottom: '3px',
                        color: segmentText,
                        whiteSpace: 'nowrap'
                      }}>
                        P
                      </div>
                      <div className="truncate" style={{ 
                        fontSize: '13px',
                        fontWeight: 700,
                        color: isSimulatedPChanged ? '#2563eb' : segmentText,
                        textAlign: 'center',
                        lineHeight: '1.2',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }} title={formatNumber(totalPeople, 1)}>
                        {formatNumber(totalPeople, 1)}
                      </div>
                    </div>
                    {/* Segmento TP - Tempo de Processo */}
                    <div 
                      className="flex-1 flex flex-col items-center justify-center"
                      style={{ 
                        backgroundColor: segmentBg,
                        color: segmentText,
                        borderRight: `1px solid ${ribbonColorBg || sectorColor || layoutTheme?.accent || minimalTheme.accent || minimalTheme.border}`,
                        padding: '4px 2px',
                        minWidth: 0
                      }}
                    >
                      <div style={{ 
                        fontSize: '8px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        opacity: 0.7,
                        marginBottom: '3px',
                        color: segmentText,
                        whiteSpace: 'nowrap'
                      }}>
                        TP
                      </div>
                      <div className="truncate" style={{ 
                        fontSize: '12px',
                        fontWeight: 700,
                        color: segmentText,
                        textAlign: 'center',
                        lineHeight: '1.2',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }} title={tpValue}>
                        {tpValue}
                      </div>
                    </div>
                    {/* Segmento TRA - Tempo de Retrabalho */}
                    <div 
                      className="flex-1 flex flex-col items-center justify-center"
                      style={{ 
                        backgroundColor: segmentBg,
                        color: segmentText,
                        borderBottomRightRadius: '12px',
                        padding: '4px 2px',
                        minWidth: 0
                      }}
                    >
                      <div style={{ 
                        fontSize: '8px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        opacity: 0.7,
                        marginBottom: '3px',
                        color: segmentText,
                        whiteSpace: 'nowrap'
                      }}>
                        TRA
                      </div>
                      <div className="truncate" style={{ 
                        fontSize: '12px',
                        fontWeight: 700,
                        color: segmentText,
                        textAlign: 'center',
                        lineHeight: '1.2',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }} title={traValue}>
                        {traValue}
                      </div>
                    </div>
                  </div>
                );
              })()}
              
              {/* Desperdícios - Design refinado */}
              {data.desperdicios && data.desperdicios.length > 0 && (
                <div className="mt-auto flex flex-wrap" style={{ 
                  paddingTop: '8px',
                  marginTop: '8px',
                  borderTop: `1px solid ${data.layoutColor === 'white' ? '#e5e7eb' : (layoutTheme?.border || minimalTheme.border || '#e5e7eb')}`,
                  gap: '4px',
                  flexShrink: 0
                }}>
                  {data.desperdicios.map((waste) => {
                    const wasteConfig = {
                      defeito: { label: 'Defeitos', color: '#ef4444', bg: '#fee2e2' },
                      espera: { label: 'Espera', color: '#f97316', bg: '#ffedd5' },
                      transporte: { label: 'Transporte', color: '#3b82f6', bg: '#dbeafe' },
                      superproducao: { label: 'Superprodução', color: '#a855f7', bg: '#f3e8ff' },
                      estoque: { label: 'Estoque', color: '#eab308', bg: '#fef9c3' },
                      movimento: { label: 'Movimento', color: '#06b6d4', bg: '#cffafe' },
                      processamento: { label: 'Proc. Extra', color: '#6b7280', bg: '#f3f4f6' },
                      talento: { label: 'Talento', color: '#ec4899', bg: '#fce7f3' },
                    }[waste] || { label: waste, color: '#6b7280', bg: '#f3f4f6' };
                    
                    return (
                      <div
                        key={waste}
                        className="font-semibold uppercase rounded-md"
                        style={{
                          padding: '4px 8px',
                          fontSize: '9px',
                          letterSpacing: '0.05em',
                          backgroundColor: wasteConfig.bg,
                          color: wasteConfig.color,
                          borderLeft: `2px solid ${wasteConfig.color}`,
                        }}
                      >
                        {wasteConfig.label}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      ) : effectiveLayoutType === 'neon' ? (
        // LAYOUT NEON  Dark industrial com acento neon brilhante
        (() => {
          const neonAccent = ribbonColorBg || sectorColor || '#00e5ff';
          const neonBodyText = '#f0f8ff';
          const neonDimText = 'rgba(255,255,255,0.62)';
          const darkBg = '#0d1117';
          const darkBg2 = '#161b22';
          const neonGlow = selected
            ? '0 0 0 2px rgba(59,130,246,0.35)'
            : `0 0 0 1px ${neonAccent}44, 0 0 14px ${neonAccent}33`;
          const neonBorder = selected ? '#3b82f6' : neonAccent;

          const tpValueN = ltInMinutes > 0 ? formatTimeDisplay(ltInMinutes, customUnitLT) : '0';
          const traValueN = ttInMinutes > 0 ? formatTimeDisplay(ttInMinutes, customUnitTT) : '0';
          const totalPeopleN = effectivePValue > 0 ? effectivePValue : 1;
          const titleLen = (effectiveAtividade || 'Nova Atividade').length;
          const titleFontSize = userFontSize
            ? `${userFontSize}px`
            : titleLen <= 14 ? '15px' : titleLen <= 28 ? '13px' : titleLen <= 45 ? '12px' : '10px';

          const metrics = [
            { label: 'P',   value: formatNumber(totalPeopleN, 1) },
            { label: 'TP',  value: tpValueN },
            { label: 'TRA', value: traValueN },
          ];

          return (
            <div
              className={`w-full flex flex-col overflow-hidden ${isInitial || isFinal ? 'rounded-full' : 'rounded'} ${simulationHeatmapClasses}`}
              style={{
                backgroundColor: isSimCrisis ? undefined : (heatmapBg || darkBg),
                border: `2px solid ${neonBorder}`,
                boxShadow: bottleneckGlow ?? neonGlow,
                animation: bottleneckAnimation,
                minHeight: cardHeight,
                height: cardHeight,
                opacity: showGhostStyle ? 0.4 : 1,
                filter: showGhostStyle ? 'grayscale(100%)' : 'none',
                boxSizing: 'border-box' as const,
                transition: 'box-shadow 300ms ease, border-color 300ms ease',
                position: 'relative' as const,
              }}
            >
              {/* Faixa superior neon */}
              {!isInitial && !isFinal && !data.vsmIcon && (
                <div style={{
                  height: '4px',
                  flexShrink: 0,
                  background: `linear-gradient(90deg, transparent 5%, ${neonAccent} 28%, ${neonAccent} 72%, transparent 95%)`,
                  boxShadow: `0 0 10px ${neonAccent}bb`,
                  opacity: 1,
                }} />
              )}

              {/* Badge row */}
              {!isInitial && !isFinal && !data.vsmIcon && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 7px 0 7px', height: '14px', flexShrink: 0, pointerEvents: 'none', position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden', maxWidth: '40%' }}>
                    {sectorName && (
                      <span style={{ fontSize: '9px', fontWeight: 400, letterSpacing: '0.05em', lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: neonDimText, userSelect: 'none' as const }} title={sectorName}>{sectorName.toUpperCase()}</span>
                    )}
                  </div>
                  {data.numeroSequencial !== undefined && data.numeroSequencial !== null && (
                    <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', fontSize: '9px', fontWeight: 400, fontFamily: "'Roboto Mono', monospace", letterSpacing: '0.05em', lineHeight: 1, color: neonDimText, userSelect: 'none' as const, whiteSpace: 'nowrap' }}>
                      #{String(data.numeroSequencial).padStart(3, '0')}
                    </span>
                  )}
                  {showPercentBadge && (() => {
                    const occValue = loadValue;
                    const isCritical = occValue > 100;
                    const isWarning = occValue > 85;
                    const val = heatmapMode && heatmapLevel ? Math.round(heatmapLevel.value) : occValue;
                    const isCrit2 = heatmapMode && heatmapLevel ? val > 100 : isCritical;
                    const isWarn2 = heatmapMode && heatmapLevel ? val > 85 : isWarning;
                    return (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '9px', fontWeight: isCrit2 ? 600 : 400, color: isCrit2 ? '#f87171' : neonDimText, lineHeight: 1, userSelect: 'none' as const, flexShrink: 0 }}>
                        {!isCrit2 && <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: isWarn2 ? '#f59e0b' : neonAccent, display: 'inline-block', boxShadow: `0 0 5px ${isWarn2 ? '#f59e0b' : neonAccent}` }} />}
                        {heatmapMode && heatmapLevel ? `${val}%` : `${occValue.toFixed(1)}%`}
                      </span>
                    );
                  })()}
                </div>
              )}

              {/* Ttulo */}
              {!isInitial && !isFinal && !data.vsmIcon && (
                <div
                  className="flex items-center justify-center px-3"
                  style={{ flex: 1, minHeight: 0, paddingBottom: '4px', paddingTop: '6px', background: `${neonAccent}12`, borderTop: `1px solid ${neonAccent}22`, borderBottom: `1px solid ${neonAccent}22` }}
                >
                  {renderEditableTitle(
                    effectiveAtividade || 'Nova Atividade',
                    {
                      color: neonBodyText,
                      fontSize: titleFontSize,
                      fontWeight: 700,
                      lineHeight: 1.3,
                      wordBreak: 'break-word' as const,
                      textAlign: 'center' as const,
                      textShadow: `0 0 12px ${neonAccent}55`,
                    },
                    'flex-1'
                  )}
                </div>
              )}

              {/* Mtricas inferiores */}
              {!isInitial && !isFinal && !data.vsmIcon && (
                <div style={{ padding: '0 6px 7px 6px', display: 'flex', flexDirection: 'column' as const, gap: '4px', flexShrink: 0 }}>
                  {/* QUEM / COMO */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                    {[
                      { label: 'QUEM', value: formatResourcesDisplay(data, organization) },
                      { label: 'COMO', value: effectiveSistema || '' },
                    ].map(({ label, value }) => (
                      <div key={label} style={{ backgroundColor: darkBg2, borderRadius: '6px', padding: '3px 6px', border: `1px solid ${neonAccent}30`, minWidth: 0 }}>
                        <div style={{ fontSize: '7px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.1em', color: neonAccent, marginBottom: '1px', opacity: 0.9 }}>{label}</div>
                        <div className="truncate" style={{ fontSize: '10px', fontWeight: 500, color: neonBodyText, lineHeight: 1.2 }}>{value}</div>
                      </div>
                    ))}
                  </div>
                  {/* P / TP / TRA */}
                  <div style={{ display: 'flex', height: '36px', gap: '4px' }}>
                    {metrics.map(({ label, value }) => (
                      <div key={label} style={{ flex: 1, display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center', backgroundColor: darkBg2, borderRadius: '6px', border: `1px solid ${neonAccent}30`, padding: '2px 4px', minWidth: 0 }}>
                        <div style={{ fontSize: '7px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.1em', color: neonAccent, opacity: 0.9 }}>{label}</div>
                        <div className="truncate" style={{ fontSize: '12px', fontWeight: 700, color: neonBodyText, textAlign: 'center' as const, lineHeight: 1, maxWidth: '100%' }}>{value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Card inicial Neon */}
              {isInitial && (
                <div className="flex-1 flex flex-col items-center justify-center p-4 text-center rounded-full">
                  <div style={{ fontWeight: 700, fontSize: '16px', lineHeight: 1.2, marginBottom: '6px', color: neonBodyText }}>Início</div>
                  <div style={{ fontSize: '11px', marginBottom: '10px', color: neonDimText }}>{data.cliente || 'Cliente Interno'}</div>
                  <div style={{ fontSize: '22px', fontWeight: 700, color: neonAccent, textShadow: `0 0 10px ${neonAccent}` }}>
                    {data.entrada ?? 0} <span style={{ fontSize: '11px', fontWeight: 400, color: neonBodyText }}>{(data as any).unidadeDemanda || '/dia'}</span>
                  </div>
                </div>
              )}

              {/* Card final Neon */}
              {isFinal && (
                <div className="flex-1 flex items-center justify-center p-4 text-center rounded-full">
                  <div style={{ fontWeight: 700, fontSize: '20px', lineHeight: 1.2, color: neonBodyText }}>Fim</div>
                </div>
              )}
            </div>
          );
        })()
      ) : effectiveLayoutType === 'blueprint' ? (
        // LAYOUT BLUEPRINT  Planta tcnica de engenharia: navy escuro, dashed border, grid, cantos marcados
        (() => {
          const bpAccent   = ribbonColorBg || sectorColor || '#3a7bd5';
          const bpNavy     = '#0b1929';
          const bpNavy2    = '#0f2036';
          const bpText     = '#f0f8ff';
          const bpDim      = '#a8d4f0';
          const bpBorder   = selected ? '#3b82f6' : bpAccent;
          const bpDash     = selected ? 'solid' : 'dashed';

          // SVG blueprint grid (crosshatch 16px)
          const gridSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16'><path d='M 16 0 L 0 0 0 16' fill='none' stroke='rgba(58,123,213,0.12)' stroke-width='0.5'/></svg>`;
          const gridUrl  = `url("data:image/svg+xml,${encodeURIComponent(gridSvg)}")`;

          const tpValBp  = ltInMinutes  > 0 ? formatTimeDisplay(ltInMinutes,  customUnitLT) : '0';
          const traValBp = ttInMinutes  > 0 ? formatTimeDisplay(ttInMinutes,  customUnitTT) : '0';
          const pValBp   = effectivePValue > 0 ? formatNumber(effectivePValue, 1) : '1';
          const titleLen = (effectiveAtividade || 'Nova Atividade').length;
          const titleFs  = userFontSize
            ? `${userFontSize}px`
            : titleLen <= 14 ? '14px' : titleLen <= 28 ? '12px' : titleLen <= 45 ? '11px' : '10px';

          // Colchetes de canto (   )
          const cornerSize = 8;
          const cornerStyle = (top: boolean, left: boolean): React.CSSProperties => ({
            position: 'absolute',
            width: cornerSize,
            height: cornerSize,
            borderTop:    top  ? `2px solid ${bpAccent}` : 'none',
            borderBottom: !top ? `2px solid ${bpAccent}` : 'none',
            borderLeft:   left ? `2px solid ${bpAccent}` : 'none',
            borderRight:  !left ? `2px solid ${bpAccent}` : 'none',
            top:    top  ? 3 : undefined,
            bottom: !top ? 3 : undefined,
            left:   left ? 3 : undefined,
            right:  !left ? 3 : undefined,
            zIndex: 2,
            pointerEvents: 'none' as const,
          });

          const metrics = [
            { key: 'P',   val: pValBp,   unit: 'fx' },
            { key: 'TP',  val: tpValBp,  unit: '' },
            { key: 'TRA', val: traValBp, unit: '' },
          ];

          return (
            <div
              className={`w-full flex flex-col overflow-hidden ${isInitial || isFinal ? 'rounded-full' : ''} ${simulationHeatmapClasses}`}
              style={{
                backgroundColor: isSimCrisis ? undefined : (heatmapBg || bpNavy),
                backgroundImage: !heatmapBg && !isSimCrisis ? gridUrl : undefined,
                backgroundSize: '16px 16px',
                border: `2px ${bpDash} ${bpBorder}`,
                borderRadius: isInitial || isFinal ? '9999px' : '6px',
                minHeight: cardHeight,
                height: cardHeight,
                opacity: showGhostStyle ? 0.4 : 1,
                filter: showGhostStyle ? 'grayscale(80%)' : 'none',
                boxSizing: 'border-box' as const,
                position: 'relative' as const,
                boxShadow: bottleneckGlow,
                animation: bottleneckAnimation,
              }}
            >
              {/* Cantos tcnicos */}
              {!isInitial && !isFinal && <>
                <div style={cornerStyle(true,  true)} />
                <div style={cornerStyle(true,  false)} />
                <div style={cornerStyle(false, true)} />
                <div style={cornerStyle(false, false)} />
              </>}

              {/* Stripe de acento esquerda */}
              {!isInitial && !isFinal && !data.vsmIcon && (
                <div style={{ position: 'absolute', top: 10, bottom: 10, left: 0, width: '4px', backgroundColor: bpAccent, borderRadius: '0 2px 2px 0', zIndex: 3, boxShadow: `2px 0 8px ${bpAccent}55` }} />
              )}

              {/* Badge row */}
              {!isInitial && !isFinal && !data.vsmIcon && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 8px 0 10px', height: '15px', flexShrink: 0, pointerEvents: 'none', position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden', maxWidth: '45%' }}>
                    {sectorName && (
                      <span style={{ fontSize: '8px', fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' as const, lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: bpDim, userSelect: 'none' as const }}>{sectorName}</span>
                    )}
                  </div>
                  {data.numeroSequencial !== undefined && data.numeroSequencial !== null && (
                    <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', fontSize: '9px', fontWeight: 500, fontFamily: "'Roboto Mono', monospace", letterSpacing: '0.08em', lineHeight: 1, color: bpDim, userSelect: 'none' as const, whiteSpace: 'nowrap' }}>
                      DWG-{String(data.numeroSequencial).padStart(3, '0')}
                    </span>
                  )}
                  {showPercentBadge && (() => {
                    const occ = loadValue;
                    const isCrit = occ > 100;
                    const isWarn = occ > 85;
                    const val = heatmapMode && heatmapLevel ? Math.round(heatmapLevel.value) : occ;
                    const isCrit2 = heatmapMode && heatmapLevel ? val > 100 : isCrit;
                    const isWarn2 = heatmapMode && heatmapLevel ? val > 85 : isWarn;
                    return (
                      <span style={{ fontSize: '9px', fontWeight: 500, fontFamily: "'Roboto Mono', monospace", color: isCrit2 ? '#f87171' : isWarn2 ? '#fbbf24' : bpDim, userSelect: 'none' as const, flexShrink: 0 }}>
                        {heatmapMode && heatmapLevel ? `${val}%` : `${occ.toFixed(1)}%`}
                      </span>
                    );
                  })()}
                </div>
              )}

              {/* Ttulo */}
              {!isInitial && !isFinal && !data.vsmIcon && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: '4px 12px 4px 12px', minHeight: 0, background: `${bpAccent}12`, borderTop: `1px solid ${bpAccent}33`, borderBottom: `1px solid ${bpAccent}33` }}>
                  {renderEditableTitle(
                    (effectiveAtividade || 'Nova Atividade').toUpperCase(),
                    {
                      color: bpText,
                      fontSize: titleFs,
                      fontWeight: 600,
                      fontFamily: "'Roboto Mono', monospace",
                      lineHeight: 1.35,
                      letterSpacing: '0.03em',
                      wordBreak: 'break-word' as const,
                      textAlign: 'left' as const,
                    },
                    'flex-1'
                  )}
                </div>
              )}

              {/* Separador tcnico */}
              {!isInitial && !isFinal && !data.vsmIcon && (
                <div style={{ marginLeft: 10, marginRight: 10, height: '1px', backgroundColor: bpAccent, opacity: 0.55, flexShrink: 0 }} />
              )}

              {/* Bloco QUEM/COMO */}
              {!isInitial && !isFinal && !data.vsmIcon && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', padding: '5px 10px 4px 10px', flexShrink: 0 }}>
                  {[
                    { label: 'RESP.', value: formatResourcesDisplay(data, organization) },
                    { label: 'SIST.',  value: effectiveSistema || '' },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '7px', fontWeight: 700, letterSpacing: '0.14em', color: bpAccent, marginBottom: '1px', fontFamily: "'Roboto Mono', monospace" }}>{label}</div>
                      <div className="truncate" style={{ fontSize: '10px', fontWeight: 400, color: bpText, lineHeight: 1.2, fontFamily: "'Roboto Mono', monospace" }}>{value}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Mtricas blueprint  barra horizontal com separadores verticais */}
              {!isInitial && !isFinal && !data.vsmIcon && (
                <div style={{ display: 'flex', flexShrink: 0, margin: '0 10px 8px 10px', border: `1px solid ${bpAccent}77`, borderRadius: '3px', overflow: 'hidden', backgroundColor: bpNavy2 }}>
                  {metrics.map(({ key, val }, idx) => (
                    <div
                      key={key}
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column' as const,
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '4px 2px',
                        borderLeft: idx > 0 ? `1px solid ${bpAccent}77` : 'none',
                      }}
                    >
                      <div style={{ fontSize: '7px', fontWeight: 700, letterSpacing: '0.14em', color: bpAccent, fontFamily: "'Roboto Mono', monospace" }}>{key}</div>
                      <div className="truncate" style={{ fontSize: '12px', fontWeight: 600, color: bpText, fontFamily: "'Roboto Mono', monospace", lineHeight: 1, maxWidth: '100%', textAlign: 'center' as const }}>{val}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Inicial Blueprint */}
              {isInitial && (
                <div className="flex-1 flex flex-col items-center justify-center p-4 text-center rounded-full">
                  <div style={{ fontWeight: 700, fontSize: '14px', letterSpacing: '0.12em', fontFamily: "'Roboto Mono', monospace", color: bpText, marginBottom: '6px', textTransform: 'uppercase' as const }}>Início</div>
                  <div style={{ fontSize: '10px', color: bpDim, marginBottom: '8px', fontFamily: "'Roboto Mono', monospace" }}>{data.cliente || 'Cliente Interno'}</div>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: bpAccent, fontFamily: "'Roboto Mono', monospace" }}>
                    {data.entrada ?? 0}<span style={{ fontSize: '10px', fontWeight: 400, color: bpDim }}> {(data as any).unidadeDemanda || '/dia'}</span>
                  </div>
                </div>
              )}

              {/* Final Blueprint */}
              {isFinal && (
                <div className="flex-1 flex items-center justify-center p-4 text-center rounded-full">
                  <div style={{ fontWeight: 700, fontSize: '16px', letterSpacing: '0.12em', fontFamily: "'Roboto Mono', monospace", color: bpText, textTransform: 'uppercase' as const }}>FIM</div>
                </div>
              )}
            </div>
          );
        })()
      ) : effectiveLayoutType === 'origami' ? (
        // LAYOUT ORIGAMI  papel dobrado: canto dobrado, linhas de vinco, sombra suave
        (() => {
          const ogAccent   = ribbonColorBg || sectorColor || '#e85d3a';
          const ogBg       = '#fffdf7';
          const ogText     = '#1c1917';
          const ogDim      = '#44403c';
          const ogCrease   = 'rgba(0,0,0,0.10)';
          const foldSize   = 28; // tamanho do canto dobrado
          const ogBorder   = selected ? '#3b82f6' : ogAccent;

          // Clip-path: retngulo com canto superior direito cortado
          const clipPath = `polygon(0 0, calc(100% - ${foldSize}px) 0, 100% ${foldSize}px, 100% 100%, 0 100%)`;

          const tpValOg  = ltInMinutes  > 0 ? formatTimeDisplay(ltInMinutes,  customUnitLT) : '0';
          const traValOg = ttInMinutes  > 0 ? formatTimeDisplay(ttInMinutes,  customUnitTT) : '0';
          const pValOg   = effectivePValue > 0 ? formatNumber(effectivePValue, 1) : '1';
          const titleLen = (effectiveAtividade || 'Nova Atividade').length;
          const titleFs  = userFontSize
            ? `${userFontSize}px`
            : titleLen <= 14 ? '15px' : titleLen <= 28 ? '13px' : titleLen <= 45 ? '12px' : '10px';

          return (
            <div
              className={`w-full flex flex-col ${isInitial || isFinal ? 'rounded-full overflow-hidden' : ''} ${simulationHeatmapClasses}`}
              style={{
                position: 'relative' as const,
                backgroundColor: 'transparent',
                minHeight: cardHeight,
                height: cardHeight,
                opacity: showGhostStyle ? 0.4 : 1,
                filter: showGhostStyle ? 'grayscale(80%)' : 'none',
              }}
            >
              {/* Corpo principal com clip-path (vinco) */}
              <div style={{
                position: 'absolute' as const,
                inset: 0,
                backgroundColor: isSimCrisis ? undefined : (heatmapBg || ogBg),
                clipPath: (!isInitial && !isFinal) ? clipPath : undefined,
                border: `2px solid ${ogBorder}`,
                borderRadius: isInitial || isFinal ? '9999px' : '6px',
                boxShadow: bottleneckGlow ?? '3px 4px 14px rgba(0,0,0,0.18), 1px 2px 0 rgba(0,0,0,0.08)',
                animation: bottleneckAnimation,
                zIndex: 0,
              }} />

              {/* Linha de vinco horizontal  efeito papel dobrado */}
              {!isInitial && !isFinal && !data.vsmIcon && (
                <div style={{
                  position: 'absolute' as const,
                  top: '68px',
                  left: 0,
                  right: 0,
                  height: '1px',
                  background: `linear-gradient(90deg, ${ogCrease} 0%, rgba(0,0,0,0.20) 35%, ${ogCrease} 100%)`,
                  zIndex: 1,
                  pointerEvents: 'none' as const,
                }} />
              )}

              {/* Tringulo do canto dobrado */}
              {!isInitial && !isFinal && !data.vsmIcon && (
                <div style={{
                  position: 'absolute' as const,
                  top: 0,
                  right: 0,
                  width: 0,
                  height: 0,
                  borderStyle: 'solid',
                  borderWidth: `0 ${foldSize}px ${foldSize}px 0`,
                  borderColor: `transparent ${ogAccent} transparent transparent`,
                  zIndex: 2,
                  filter: 'drop-shadow(-1px 1px 1px rgba(0,0,0,0.15))',
                  pointerEvents: 'none' as const,
                }} />
              )}
              {/* Sombra interna do canto (tringulo escuro  efeito profundidade) */}
              {!isInitial && !isFinal && !data.vsmIcon && (
                <div style={{
                  position: 'absolute' as const,
                  top: 0,
                  right: 0,
                  width: 0,
                  height: 0,
                  borderStyle: 'solid',
                  borderWidth: `${foldSize}px 0 0 ${foldSize}px`,
                  borderColor: `transparent transparent transparent rgba(0,0,0,0.10)`,
                  zIndex: 3,
                  pointerEvents: 'none' as const,
                }} />
              )}

              {/* Contedo sobre tudo */}
              <div style={{ position: 'relative' as const, zIndex: 4, display: 'flex', flexDirection: 'column' as const, height: '100%' }}>

                {/* Badge row */}
                {!isInitial && !isFinal && !data.vsmIcon && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 34px 0 8px', height: '16px', flexShrink: 0, pointerEvents: 'none', position: 'relative' as const }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden', maxWidth: '40%' }}>
                      {sectorName && (
                        <span style={{ fontSize: '8px', fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase' as const, color: ogDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', userSelect: 'none' as const }}>{sectorName}</span>
                      )}
                    </div>
                    {data.numeroSequencial !== undefined && data.numeroSequencial !== null && (
                      <span style={{ position: 'absolute' as const, left: '50%', transform: 'translateX(-50%)', fontSize: '8px', color: ogDim, fontWeight: 400, userSelect: 'none' as const, whiteSpace: 'nowrap' }}>#{String(data.numeroSequencial).padStart(3,'0')}</span>
                    )}
                    {showPercentBadge && (() => {
                      const occ = loadValue; const isCrit = occ > 100; const isWarn = occ > 85;
                      const val = heatmapMode && heatmapLevel ? Math.round(heatmapLevel.value) : occ;
                      const isCrit2 = heatmapMode && heatmapLevel ? val > 100 : isCrit;
                      const isWarn2 = heatmapMode && heatmapLevel ? val > 85 : isWarn;
                      return (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '8px', fontWeight: isCrit2 ? 600 : 400, color: isCrit2 ? '#ef4444' : isWarn2 ? '#f59e0b' : ogDim, lineHeight: 1, userSelect: 'none' as const, flexShrink: 0 }}>
                          {!isCrit2 && <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: isWarn2 ? '#f59e0b' : ogAccent, display: 'inline-block' }} />}
                          {heatmapMode && heatmapLevel ? `${val}%` : `${occ.toFixed(1)}%`}
                        </span>
                      );
                    })()}
                  </div>
                )}

                {/* Ttulo */}
                {!isInitial && !isFinal && !data.vsmIcon && (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: '4px 32px 4px 8px', minHeight: 0 }}>
                    {renderEditableTitle(
                      effectiveAtividade || 'Nova Atividade',
                      {
                        color: ogText,
                        fontSize: titleFs,
                        fontWeight: 600,
                        lineHeight: 1.35,
                        wordBreak: 'break-word' as const,
                        textAlign: 'left' as const,
                      },
                      'flex-1'
                    )}
                  </div>
                )}

                {/* Linha de vinco  rea inferior */}
                {!isInitial && !isFinal && !data.vsmIcon && (
                  <div style={{ padding: '0 8px 7px 8px', display: 'flex', flexDirection: 'column' as const, gap: '5px', flexShrink: 0 }}>
                    {/* Quem / Sistema */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {[
                        { label: 'Quem', value: formatResourcesDisplay(data, organization) },
                        { label: 'Sistema', value: effectiveSistema || '' },
                      ].map(({ label, value }) => (
                        <div key={label} style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '7px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.1em', color: ogAccent, marginBottom: '1px' }}>{label}</div>
                          <div className="truncate" style={{ fontSize: '10px', color: ogText, lineHeight: 1.2 }}>{value}</div>
                        </div>
                      ))}
                    </div>
                    {/* P / TP / TRA  chips de papel */}
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {[
                        { k: 'P',   v: pValOg },
                        { k: 'TP',  v: tpValOg },
                        { k: 'TRA', v: traValOg },
                      ].map(({ k, v }) => (
                        <div key={k} style={{ flex: 1, backgroundColor: `${ogAccent}12`, border: `1px solid ${ogAccent}30`, borderRadius: '4px', padding: '3px 4px', display: 'flex', flexDirection: 'column' as const, alignItems: 'center', minWidth: 0 }}>
                          <div style={{ fontSize: '7px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.1em', color: ogAccent }}>{k}</div>
                          <div className="truncate" style={{ fontSize: '12px', fontWeight: 700, color: ogText, lineHeight: 1, maxWidth: '100%', textAlign: 'center' as const }}>{v}</div>
                        </div>
                      ))}
                    </div>
                    {/* % badge inline */}
                    {showPercentBadge && (() => {
                      const occ = loadValue; const isCrit = occ > 100; const isWarn = occ > 85;
                      const val = heatmapMode && heatmapLevel ? Math.round(heatmapLevel.value) : occ;
                      const isCrit2 = heatmapMode && heatmapLevel ? val > 100 : isCrit;
                      const isWarn2 = heatmapMode && heatmapLevel ? val > 85 : isWarn;
                      const barW = Math.min(100, val);
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ flex: 1, height: '3px', backgroundColor: `${ogAccent}20`, borderRadius: '2px', overflow: 'hidden' }}>
                            <div style={{ width: `${barW}%`, height: '100%', backgroundColor: isCrit2 ? '#ef4444' : isWarn2 ? '#f59e0b' : ogAccent, borderRadius: '2px' }} />
                          </div>
                          <span style={{ fontSize: '8px', fontWeight: 600, color: isCrit2 ? '#ef4444' : isWarn2 ? '#f59e0b' : ogDim, flexShrink: 0 }}>
                            {heatmapMode && heatmapLevel ? `${val}%` : `${occ.toFixed(1)}%`}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Inicial Origami */}
                {isInitial && (
                  <div className="flex-1 flex flex-col items-center justify-center p-4 text-center rounded-full">
                    <div style={{ fontWeight: 700, fontSize: '16px', color: ogText, marginBottom: '4px' }}>Início</div>
                    <div style={{ fontSize: '11px', color: ogDim, marginBottom: '8px' }}>{data.cliente || 'Cliente Interno'}</div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: ogAccent }}>{data.entrada ?? 0}<span style={{ fontSize: '11px', fontWeight: 400, color: ogDim }}> {(data as any).unidadeDemanda || '/dia'}</span></div>
                  </div>
                )}
                {/* Final Origami */}
                {isFinal && (
                  <div className="flex-1 flex items-center justify-center p-4 text-center rounded-full">
                    <div style={{ fontWeight: 700, fontSize: '20px', color: ogText }}>Fim</div>
                  </div>
                )}
              </div>
            </div>
          );
        })()
      ) : effectiveLayoutType === 'kanban' ? (
        // LAYOUT KANBAN  board card: header colorido com accent, borda esquerda espessa, chips de métricas
        (() => {
          const knAccentRaw = ribbonColorBg || sectorColor || '#7c3aed';

          // Garantir que o accent seja sempre escuro o suficiente para header legvel
          const darkenIfLight = (hex: string): string => {
            const h = hex.replace('#', '');
            if (h.length !== 6) return hex;
            const r = parseInt(h.substring(0, 2), 16);
            const g = parseInt(h.substring(2, 4), 16);
            const b = parseInt(h.substring(4, 6), 16);
            const luminance = (r * 299 + g * 587 + b * 114) / 1000;
            if (luminance <= 140) return hex; // j escuro o suficiente
            // Escurecer: multiplicar canais por fator
            const factor = luminance > 220 ? 0.25 : luminance > 180 ? 0.40 : 0.55;
            const nr = Math.round(r * factor);
            const ng = Math.round(g * factor);
            const nb = Math.round(b * factor);
            return '#' + [nr, ng, nb].map(x => x.toString(16).padStart(2, '0')).join('');
          };

          const knAccent   = knAccentRaw;
          const knHeader   = darkenIfLight(knAccentRaw); // sempre escuro para header
          const knBg       = '#ffffff';
          const knBodyBg   = `${knHeader}0c`;
          const knText     = '#111111';
          const knDim      = '#3d3d3d';
          const knBorder   = selected ? '#3b82f6' : knHeader;

          const tpValKn  = ltInMinutes  > 0 ? formatTimeDisplay(ltInMinutes,  customUnitLT) : '';
          const traValKn = ttInMinutes  > 0 ? formatTimeDisplay(ttInMinutes,  customUnitTT) : '';
          const pValKn   = effectivePValue > 0 ? formatNumber(effectivePValue, 1) : '1';
          const titleLen = (effectiveAtividade || 'Nova Atividade').length;
          const titleFs  = userFontSize
            ? `${userFontSize}px`
            : titleLen <= 16 ? '14px' : titleLen <= 32 ? '12px' : titleLen <= 50 ? '11px' : '10px';

          const occVal   = heatmapMode && heatmapLevel ? Math.round(heatmapLevel.value) : loadValue;
          const isCritKn = occVal > 100;
          const isWarnKn = occVal > 85;
          const statusColor = isCritKn ? '#ef4444' : isWarnKn ? '#f59e0b' : knHeader;
          const statusLabel = isCritKn ? 'Crítico' : isWarnKn ? 'Ateno' : 'Em fluxo';

          return (
            <div
              className={`w-full flex flex-col overflow-hidden ${isInitial || isFinal ? 'rounded-full' : ''} ${simulationHeatmapClasses}`}
              style={{
                backgroundColor: isSimCrisis ? undefined : (heatmapBg || knBg),
                border: `2px solid ${knBorder}`,
                borderLeft: `6px solid ${isSimCrisis ? undefined : (selected ? '#3b82f6' : knHeader)}`,
                borderRadius: isInitial || isFinal ? '9999px' : '8px',
                boxShadow: bottleneckGlow ?? `0 2px 10px rgba(0,0,0,0.16), 0 0 0 1px ${knHeader}33`,
                animation: bottleneckAnimation,
                minHeight: cardHeight,
                height: cardHeight,
                opacity: showGhostStyle ? 0.4 : 1,
                filter: showGhostStyle ? 'grayscale(80%)' : 'none',
                boxSizing: 'border-box' as const,
                position: 'relative' as const,
              }}
            >
              {/* Header colorido com accent */}
              {!isInitial && !isFinal && !data.vsmIcon && (
                <div style={{
                  backgroundColor: heatmapBg || knHeader,
                  padding: '5px 8px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  flexShrink: 0, gap: '4px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', overflow: 'hidden', flex: 1, minWidth: 0 }}>
                    {sectorName && (
                      <span style={{ fontSize: '9px', fontWeight: 700, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase' as const, letterSpacing: '0.1em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', userSelect: 'none' as const }}>{sectorName}</span>
                    )}
                    {data.numeroSequencial !== undefined && data.numeroSequencial !== null && (
                      <span style={{ fontSize: '9px', fontWeight: 500, color: 'rgba(255,255,255,0.7)', userSelect: 'none' as const, flexShrink: 0 }}>#{String(data.numeroSequencial).padStart(3,'0')}</span>
                    )}
                  </div>
                  {/* % badge  padrao igual aos outros layouts */}
                  {showPercentBadge && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '9px', fontWeight: isCritKn ? 600 : 400, color: isCritKn ? '#fca5a5' : isWarnKn ? '#fde68a' : 'rgba(255,255,255,0.9)', lineHeight: 1, userSelect: 'none' as const, flexShrink: 0 }}>
                      {!isCritKn && <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: isWarnKn ? '#fde68a' : '#ffffff', display: 'inline-block' }} />}
                      {heatmapMode && heatmapLevel ? `${occVal}%` : `${loadValue.toFixed(1)}%`}
                    </span>
                  )}
                </div>
              )}

              {/* Ttulo */}
              {!isInitial && !isFinal && !data.vsmIcon && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: '6px 8px 4px 8px', minHeight: 0 }}>
                  {renderEditableTitle(
                    effectiveAtividade || 'Nova Atividade',
                    {
                      color: knText,
                      fontSize: titleFs,
                      fontWeight: 700,
                      lineHeight: 1.35,
                      wordBreak: 'break-word' as const,
                      textAlign: 'left' as const,
                    },
                    'flex-1'
                  )}
                </div>
              )}

              {/* Divisor */}
              {!isInitial && !isFinal && !data.vsmIcon && (
                <div style={{ height: '1px', backgroundColor: `${knHeader}40`, marginLeft: '8px', marginRight: '8px', flexShrink: 0 }} />
              )}

              {/* Bloco de dados: Quem e Sistema */}
              {!isInitial && !isFinal && !data.vsmIcon && (
                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '3px', padding: '5px 8px 4px 8px', flexShrink: 0, backgroundColor: heatmapBg ? undefined : knBodyBg }}>
                  {[
                    { label: 'QUEM',    value: formatResourcesDisplay(data, organization) },
                    { label: 'SISTEMA', value: effectiveSistema || '' },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
                      <span style={{ fontSize: '8px', fontWeight: 800, color: knHeader, flexShrink: 0, letterSpacing: '0.08em', minWidth: '46px' }}>{label}</span>
                      <span className="truncate" style={{ fontSize: '10px', fontWeight: 500, color: knText, flex: 1 }}>{value}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Footer métricas: P / TP / TRA em chips */}
              {!isInitial && !isFinal && !data.vsmIcon && (
                <div style={{ display: 'flex', gap: '4px', padding: '4px 8px 7px 8px', flexShrink: 0 }}>
                  {[
                    { k: 'P',   v: pValKn },
                    { k: 'TP',  v: tpValKn },
                    { k: 'TRA', v: traValKn },
                  ].map(({ k, v }) => (
                    <div key={k} style={{ flex: 1, display: 'flex', flexDirection: 'column' as const, alignItems: 'center', backgroundColor: `${knHeader}18`, borderRadius: '6px', padding: '3px 4px', border: `1.5px solid ${knHeader}77`, minWidth: 0, overflow: 'hidden' }}>
                      <span style={{ fontSize: '7px', fontWeight: 800, color: knHeader, letterSpacing: '0.1em' }}>{k}</span>
                      <span className="truncate" style={{ fontSize: '11px', fontWeight: 700, color: knText, maxWidth: '100%', textAlign: 'center' as const }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Inicial Kanban */}
              {isInitial && (
                <div className="flex-1 flex flex-col items-center justify-center p-4 text-center rounded-full">
                  <div style={{ fontWeight: 700, fontSize: '16px', color: knText, marginBottom: '4px' }}>Início</div>
                  <div style={{ fontSize: '11px', color: knDim, marginBottom: '8px' }}>{data.cliente || 'Cliente Interno'}</div>
                  <div style={{ fontSize: '22px', fontWeight: 700, color: knHeader }}>{data.entrada ?? 0}<span style={{ fontSize: '11px', fontWeight: 400, color: knDim }}> {(data as any).unidadeDemanda || '/dia'}</span></div>
                </div>
              )}
              {/* Final Kanban */}
              {isFinal && (
                <div className="flex-1 flex items-center justify-center p-4 text-center rounded-full">
                  <div style={{ fontWeight: 700, fontSize: '20px', color: knText }}>Fim</div>
                </div>
              )}
            </div>
          );
        })()
      ) : effectiveLayoutType === 'ticket' ? (
        // LAYOUT TICKET  bilhete com faixa colorida, picote e stub de métricas
        (() => {
          const tkAccentRaw = ribbonColorBg || sectorColor || '#d97706';
          const darkenIfLight = (hex: string): string => {
            const h = hex.replace('#', '');
            if (h.length !== 6) return hex;
            const r = parseInt(h.substring(0, 2), 16);
            const g = parseInt(h.substring(2, 4), 16);
            const b = parseInt(h.substring(4, 6), 16);
            const luminance = (r * 299 + g * 587 + b * 114) / 1000;
            if (luminance <= 140) return hex;
            const factor = luminance > 220 ? 0.25 : luminance > 180 ? 0.40 : 0.55;
            const nr = Math.round(r * factor), ng = Math.round(g * factor), nb = Math.round(b * factor);
            return '#' + [nr, ng, nb].map(x => x.toString(16).padStart(2, '0')).join('');
          };
          const tkHeader     = darkenIfLight(tkAccentRaw);
          const tkBg         = '#fffef5';
          const tkBorder     = selected ? '#3b82f6' : `${tkHeader}99`;
          const tkHeaderText = (() => {
            const h = tkHeader.replace('#', '');
            if (h.length !== 6) return '#ffffff';
            const r = parseInt(h.substring(0,2),16), g = parseInt(h.substring(2,4),16), b = parseInt(h.substring(4,6),16);
            return (r*299+g*587+b*114)/1000 < 140 ? '#ffffff' : '#1a1a1a';
          })();
          const tpVal   = ltInMinutes  > 0 ? formatTimeDisplay(ltInMinutes,  customUnitLT)  : '';
          const traVal  = ttInMinutes  > 0 ? formatTimeDisplay(ttInMinutes,  customUnitTT)  : '';
          const pVal    = effectivePValue > 0 ? formatNumber(effectivePValue, 1) : '';
          const quemVal = data.quem || (data.resources && data.resources.length > 0 ? ((data.resources[0] as any).roleName || null) : null);
          const titleLen = (effectiveAtividade || 'Nova Atividade').length;
          const titleFs  = userFontSize ? `${userFontSize}px`
            : titleLen <= 14 ? '15px' : titleLen <= 28 ? '13px' : titleLen <= 45 ? '11px' : '10px';

          if (isInitial) return (
            <div style={{
              width: '100%', height: '100%', borderRadius: '50%',
              backgroundColor: tkHeader, border: `2.5px solid ${tkBorder}`,
              display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 4px 14px ${tkHeader}55`, padding: '10px',
              opacity: showGhostStyle ? 0.4 : 1,
            }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: `${tkHeaderText}88`, letterSpacing: '0.12em', textTransform: 'uppercase' as const, marginBottom: '4px' }}>Início</div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: tkHeaderText, marginBottom: '6px' }}>{data.cliente || 'Cliente Interno'}</div>
              <div style={{ fontSize: '24px', fontWeight: 900, color: tkHeaderText, lineHeight: 1 }}>
                {data.entrada ?? 0}<span style={{ fontSize: '11px', fontWeight: 400, color: `${tkHeaderText}bb` }}> {(data as any).unidadeDemanda || '/dia'}</span>
              </div>
            </div>
          );

          if (isFinal) return (
            <div style={{
              width: '100%', height: '100%', borderRadius: '50%',
              backgroundColor: tkHeader, border: `2.5px solid ${tkBorder}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 4px 14px ${tkHeader}55`,
              opacity: showGhostStyle ? 0.4 : 1,
            }}>
              <div style={{ fontSize: '20px', fontWeight: 900, color: tkHeaderText, letterSpacing: '0.06em' }}>FIM</div>
            </div>
          );

          return (
            <div style={{
              width: '100%', height: `${cardHeight}px`,
              backgroundColor: tkBg,
              border: `1.5px solid ${tkBorder}`,
              borderRadius: '10px',
              overflow: 'hidden',
              boxShadow: '0 3px 10px rgba(0,0,0,0.15), 0 1px 3px rgba(0,0,0,0.08)',
              display: 'flex', flexDirection: 'column' as const,
              opacity: showGhostStyle ? 0.4 : 1,
              filter: showGhostStyle ? 'grayscale(100%)' : 'none',
            }}>
              {/* Header strip */}
              <div style={{ backgroundColor: tkHeader, padding: '5px 10px 7px', flexShrink: 0 }}>
                {/* Micro row: sector + ID + % */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
                  <span style={{ fontSize: '8px', fontWeight: 600, color: `${tkHeaderText}88`, letterSpacing: '0.1em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const, maxWidth: '40%' }}>
                    {sectorName?.toUpperCase() || ''}
                  </span>
                  {data.numeroSequencial !== undefined && data.numeroSequencial !== null && (
                    <span style={{ fontSize: '8px', fontFamily: "'Roboto Mono', monospace", color: `${tkHeaderText}cc`, letterSpacing: '0.05em' }}>
                      #{String(data.numeroSequencial).padStart(3, '0')}
                    </span>
                  )}
                  {showPercentBadge && (
                    <span style={{ fontSize: '8px', color: `${tkHeaderText}bb` }}> {loadValue.toFixed(1)}%</span>
                  )}
                </div>
                {/* Title */}
                <div style={{ fontSize: titleFs, fontWeight: 500, color: tkHeaderText, textTransform: 'uppercase' as const, letterSpacing: '0.04em', lineHeight: 1.2, textAlign: 'center' as const }}>
                  {effectiveAtividade || 'Nova Atividade'}
                </div>
              </div>

              {/* Perforated divider */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '14px', flexShrink: 0, gap: '4px', backgroundColor: tkBg }}>
                {Array.from({ length: 18 }).map((_, i) => (
                  <div key={i} style={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: '#b0b8c4', flexShrink: 0 }} />
                ))}
              </div>

              {/* Body */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' as const, padding: '4px 10px 2px', backgroundColor: tkBg, overflow: 'hidden' }}>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '3px', overflow: 'hidden' }}>
                  <span style={{ fontSize: '9px', fontWeight: 700, color: tkHeader, letterSpacing: '0.1em', flexShrink: 0 }}>QUEM</span>
                  <span style={{ fontSize: '11px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{quemVal || '-'}</span>
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', overflow: 'hidden' }}>
                  <span style={{ fontSize: '9px', fontWeight: 700, color: tkHeader, letterSpacing: '0.1em', flexShrink: 0 }}>SIS.</span>
                  <span style={{ fontSize: '11px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{effectiveSistema || '-'}</span>
                </div>
              </div>

              {/* Footer stub com picote acima */}
              <div style={{ borderTop: '1.5px dashed #c9d0db', backgroundColor: `${tkHeader}12`, display: 'flex', justifyContent: 'space-around', padding: '5px 8px 6px', flexShrink: 0 }}>
                {[{ k: 'P', v: pVal }, { k: 'TP', v: tpVal }, { k: 'TRA', v: traVal }].map(({ k, v }) => (
                  <div key={k} style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'center' }}>
                    <span style={{ fontSize: '7px', fontWeight: 700, color: tkHeader, letterSpacing: '0.1em' }}>{k}</span>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#1a1a1a' }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()
      ) : (
        // LAYOUT DEFAULT (incluindo card inicial e final) - Enterprise Premium Style
        // Fundo sempre branco, cor aplicada apenas no header e borda
        <div 
          className={`w-full flex flex-col overflow-hidden border-2 ${isInitial || isFinal ? 'rounded-full' : 'rounded'} ${(heatmapBg || sectorBg) ? '' : 'bg-white dark:bg-slate-900'} ${simulationHeatmapClasses}`}
          style={(() => {
            // Se selecionado ou hover, usar borda azul
            if (selected && !isSimOverloaded) {
              return { 
                minHeight: cardHeight,
                height: isAction ? 'auto' : `${cardHeight}px`,
                border: '2px solid #3b82f6',
                backgroundColor: heatmapBg || sectorBg,
                opacity: showGhostStyle ? 0.4 : 1,
                filter: showGhostStyle ? 'grayscale(100%)' : 'none',
                outline: 'none',
              };
            }
            
            return {
              minHeight: cardHeight,
              height: isAction ? 'auto' : `${cardHeight}px`,
              borderColor: semanticBorderVisual.color,
              borderWidth: semanticBorderVisual.width,
              backgroundColor: heatmapBg || sectorBg,
              boxShadow: undefined,
              transition: simIsActive ? 'border-color 300ms ease, background-color 300ms ease' : undefined,
              opacity: showGhostStyle ? 0.4 : 1,
              filter: showGhostStyle ? 'grayscale(100%)' : 'none',
              outline: 'none',
            };
          })()}
        >
          <div
            className="flex flex-col border-b-2 relative overflow-hidden"
            // @ts-ignore
            style={(() => {
              const isDefaultLayout = !effectiveLayoutType || effectiveLayoutType === 'default';
              const baseStyle = { minHeight: '70px', marginBottom: '-3px', display: 'flex', flexDirection: 'column' };
              if (isInitial || isFinal) return { ...baseStyle, display: 'none' };
              if (isDefaultLayout) {
                if (sectorColor) {
                  const textColor = getReadableTextColor(sectorColor);
                  return { ...baseStyle, backgroundColor: sectorColor, borderBottomColor: sectorColor, borderBottomWidth: '2px', color: textColor };
                }
                const colorKey = data.layoutColor || 'green';
                const theme = CARD_THEMES[colorKey] || CARD_THEMES.green;
                return { ...baseStyle, backgroundColor: theme.headerBg, borderBottomColor: theme.border, borderBottomWidth: '2px', color: theme.textColor };
              }
              return { ...baseStyle, backgroundColor: '#f3f4f6', borderBottomColor: '#e5e7eb', color: '#374151' };
            })()}
          >
            {/* Linha superior micro: setor + #ID (esq) | # centro | % (dir) */}
            {!isInitial && !isFinal && !data.vsmIcon && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 7px 0 7px', height: '14px', flexShrink: 0, pointerEvents: 'none', position: 'relative' }}>
                {/* Esquerda: setor */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden', maxWidth: '40%' }}>
                  {sectorName && (
                    <span style={{ fontSize: '9px', fontWeight: 400, letterSpacing: '0.05em', lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.75, userSelect: 'none' }} title={sectorName}>
                      {sectorName.toUpperCase()}
                    </span>
                  )}
                </div>
                {/* Centro: nmero em destaque */}
                {data.numeroSequencial !== undefined && data.numeroSequencial !== null && (
                  <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', fontSize: '9px', fontWeight: 400, fontFamily: "'Roboto Mono', monospace", letterSpacing: '0.05em', lineHeight: 1, opacity: 0.9, userSelect: 'none', whiteSpace: 'nowrap' }}>
                    #{String(data.numeroSequencial).padStart(3, '0')}
                  </span>
                )}
                {/* Direita: % ocupação */}
                {showPercentBadge && (() => {
                  const occValue = loadValue;
                  const isCritical = occValue > 100;
                  const isWarning = occValue > 85;
                  const headerBg = sectorColor || (data.layoutColor ? (CARD_THEMES[data.layoutColor]?.headerBg || '#10b981') : '#10b981');
                  const isLight = (() => { try { return getReadableTextColor(headerBg) === '#ffffff'; } catch { return true; } })();
                  const baseColor = isCritical ? '#991b1b' : 'currentColor';
                  const val = heatmapMode && heatmapLevel ? Math.round(heatmapLevel.value) : occValue;
                  const isCrit2 = heatmapMode && heatmapLevel ? val > 100 : isCritical;
                  const isWarn2 = heatmapMode && heatmapLevel ? val > 85 : isWarning;
                  return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '9px', fontWeight: isCrit2 ? 600 : 400, color: isCrit2 ? '#991b1b' : baseColor, backgroundColor: isCrit2 ? '#fee2e2' : 'transparent', borderRadius: isCrit2 ? '10px' : undefined, padding: isCrit2 ? '1px 5px' : '0', lineHeight: 1, userSelect: 'none', flexShrink: 0 }}>
                      {!isCrit2 && <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: isWarn2 ? '#f59e0b' : '#22c55e', display: 'inline-block' }} />}
                      {heatmapMode && heatmapLevel ? `${val}%` : `${occValue.toFixed(1)}%`}
                    </span>
                  );
                })()}
              </div>
            )}

            {/* Alertas de analise */}
            {!isInitial && !isFinal && analysisMode && (
              <div className="absolute bottom-1 left-2 flex gap-1 z-10">
                {isBottleneck && <div title="Gargalo"><AlertTriangle size={11} className="text-red-600" /></div>}
                {(() => {
                  const hasLowQuality = (data.percentCompleteAccurate || 100) < 80;
                  return hasLowQuality && !isBottleneck && <div title="Retrabalho"><AlertTriangle size={11} className="text-orange-600" /></div>;
                })()}
              </div>
            )}

            {/* Badge de Melhoria */}
            {!isInitial && !isFinal && !data.vsmIcon && (() => {
              const hasTPReduction = (data as any).originalTP !== undefined && (data as any).originalTP > 0 && (data.lt || 0) < (data as any).originalTP;
              const hasTRAReduction = (data as any).originalTRA !== undefined && (data as any).originalTRA > 0 && (data.tt || 0) < (data as any).originalTRA;
              if (!hasTPReduction && !hasTRAReduction) return null;
              let reductionPercent = 0;
              let reductionText = '';
              if (hasTPReduction && hasTRAReduction) {
                const tpR = (((data as any).originalTP - (data.lt || 0)) / (data as any).originalTP) * 100;
                const traR = (((data as any).originalTRA - (data.tt || 0)) / (data as any).originalTRA) * 100;
                reductionPercent = Math.round(Math.max(tpR, traR));
                reductionText = `TP/TRA reduzidos`;
              } else if (hasTPReduction) {
                reductionPercent = Math.round((((data as any).originalTP - (data.lt || 0)) / (data as any).originalTP) * 100);
                reductionText = `TP melhorado`;
              } else {
                reductionPercent = Math.round((((data as any).originalTRA - (data.tt || 0)) / (data as any).originalTRA) * 100);
                reductionText = `TRA melhorado`;
              }
              return (
                <div className="absolute right-1 bg-emerald-100 text-emerald-700 text-[8px] font-bold px-1 py-0.5 rounded z-10 flex items-center gap-0.5" style={{ top: '2px' }} title={reductionText}>
                  <span>a</span><span>-{reductionPercent}%</span>
                </div>
              );
            })()}

            {/* Titulo  preenche toda a area restante abaixo dos badges */}
            {!isInitial && !isFinal && renderEditableTitle(
              effectiveAtividade || 'Nova Atividade',
              {
                fontSize: (() => {
                  if (userFontSize) return `${userFontSize}px`;
                  const title = effectiveAtividade || 'Nova Atividade';
                  const len = title.length;
                  if (len <= 14) return '18px';
                  if (len <= 28) return '14px';
                  if (len <= 45) return '13px';
                  if (len <= 60) return '12px';
                  return '10px';
                })(),
                color: userTextColor || (() => {
                  const colorKey = data.layoutColor || 'green';
                  const theme = CARD_THEMES[colorKey] || CARD_THEMES.green;
                  return sectorColor ? getReadableTextColor(sectorColor) : theme.textColor;
                })(),
                flex: 1,
                overflow: 'hidden',
                padding: (() => {
                  const len = (effectiveAtividade || 'Nova Atividade').length;
                  return len > 45 ? '12px 7px 8px 7px' : '14px 7px 2px 7px';
                })(),
                lineHeight: 1.25,
                wordBreak: 'break-word' as const,
                display: 'flex',
                alignItems: (() => {
                  const len = (effectiveAtividade || 'Nova Atividade').length;
                  return len > 45 ? 'flex-start' : 'center';
                })(),
                justifyContent: 'center',
                textAlign: 'center',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
              },
              `font-bold uppercase tracking-wider w-full line-clamp-3 ${userBold} ${userItalic}`
            )}
            {(isInitial || isFinal) && renderEditableTitle(
              isInitial ? 'Inicio' : 'Fim',
              { fontSize: '13px', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, padding: '0 8px' },
              `font-bold uppercase tracking-wider w-full text-center ${userBold} ${userItalic}`
            )}
          </div>
          <div 
            className={`flex-1 flex flex-col justify-between text-sm relative ${isInitial || isFinal ? 'rounded-full overflow-hidden' : ''}`}
            style={{
              // Fundo branco puro com gradiente sutil para profundidade
              background: 'linear-gradient(to bottom, #ffffff 0%, #fafafa 100%)',
              color: '#1f2937', // Texto sempre escuro para contraste máximo
            }}
          >
            {!isInitial && !isFinal && !data.vsmIcon && (
               <>
                {/* Corpo Principal - Layout Equilibrado */}
                <div className="flex-1 flex flex-col px-3 py-1.5 gap-1.5 overflow-hidden" style={{ paddingTop: '7px' }}>
                  {/* Seção QUEM e COMO - Layout em linha (label: valor) */}
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <div className="flex items-baseline gap-1.5">
                      <div className="text-[10px] font-normal uppercase tracking-wide" style={{ color: '#374151', minWidth: '36px', flexShrink: 0, lineHeight: '1.4' }}>
                        QUEM:
                      </div>
                      <div 
                        className="font-normal overflow-hidden flex-1 truncate" 
                        style={{ 
                          color: '#111827', 
                          lineHeight: '1.4',
                          fontSize: (() => {
                            const text = formatResourcesDisplay(data, organization);
                            const length = text.length;
                            if (length <= 20) return '14px';
                            if (length <= 35) return '13px';
                            if (length <= 50) return '12px';
                            if (length <= 70) return '11px';
                            return '10px';
                          })()
                        }}
                        title={formatResourcesDisplay(data, organization)}
                      >
                        {formatResourcesDisplay(data, organization)}
                      </div>
                    </div>
                    <div className="flex items-baseline gap-1.5">
                      <div className="text-[10px] font-normal uppercase tracking-wide" style={{ color: '#374151', minWidth: '36px', flexShrink: 0, lineHeight: '1.4' }}>
                        COMO:
                      </div>
                      <div 
                        className="font-normal overflow-hidden flex-1 truncate" 
                        style={{ 
                          color: '#111827', 
                          lineHeight: '1.4',
                          fontSize: (() => {
                            const text = effectiveSistema || '-';
                            const length = text.length;
                            if (length <= 20) return '14px';
                            if (length <= 35) return '13px';
                            if (length <= 50) return '12px';
                            if (length <= 70) return '11px';
                            return '10px';
                          })()
                        }}
                        title={effectiveSistema || '-'}
                      >
                        {effectiveSistema || '-'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Rodapé de Métricas - Grid 3 colunas igualmente distribuídas */}
                  <div className="mt-auto pt-0 pb-2 border-t flex-shrink-0" style={{ borderColor: '#e5e7eb', marginTop: '8px' }}>
                    <div className="grid grid-cols-3 gap-2">
                      {mapType === 'FACTORY' ? (
                        <>
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="text-[10px] font-normal uppercase tracking-wide" style={{ color: '#4b5563' }}>T/C</span>
                            <span className="font-normal" style={{
                              color: ltInMinutes >= 0 ? '#111827' : '#9ca3af',
                              fontSize: '11px'
                            }}>
                              {ltInMinutes >= 0 ? formatTimeDisplay(ltInMinutes, customUnitLT) : '0'}
                            </span>
                          </div>
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="text-[10px] font-normal uppercase tracking-wide" style={{ color: '#4b5563' }}>T/R</span>
                            <span className="font-normal" style={{
                              color: ttInMinutes >= 0 ? '#111827' : '#9ca3af',
                              fontSize: '11px'
                            }}>
                              {ttInMinutes >= 0 ? formatTimeDisplay(ttInMinutes, customUnitTT) : '0'}
                            </span>
                          </div>
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="text-[10px] font-normal uppercase tracking-wide" style={{ color: '#4b5563' }}>Eficcia</span>
                            <span className="font-normal" style={{ 
                              color: (data.oee || 0) > 0 ? '#111827' : '#9ca3af',
                              fontSize: '11px'
                            }}>
                              {data.oee || 0}%
                            </span>
                          </div>
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="text-[10px] font-normal uppercase tracking-wide" style={{ color: '#4b5563' }}>Lote</span>
                            <span className="font-normal" style={{ 
                              color: (data.batchSize || 0) > 0 ? '#111827' : '#9ca3af',
                              fontSize: '11px'
                            }}>
                              {data.batchSize || 0}
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex flex-col items-center gap-0">
                            <span className="text-[10px] font-normal uppercase tracking-wide" style={{ color: '#4b5563' }}>P</span>
                            <span className="font-normal" style={{ 
                              color: isSimulatedPChanged ? '#2563eb' : (effectivePValue > 0 ? '#111827' : '#9ca3af'),
                              fontSize: '14px'
                            }}>
                              {effectivePValue > 0 ? formatNumber(effectivePValue, 2) : 0}
                            </span>
                          </div>
                          <div className="flex flex-col items-center gap-0">
                            <span className="text-[10px] font-normal uppercase tracking-wide" style={{ color: '#4b5563' }}>TP</span>
                            <span className="font-normal" style={{
                              color: ltInMinutes >= 0 ? '#111827' : '#9ca3af',
                              fontSize: '14px'
                            }}>
                              {ltInMinutes >= 0 ? formatTimeDisplay(ltInMinutes, customUnitLT) : '0'}
                            </span>
                          </div>
                          <div className="flex flex-col items-center gap-0">
                            <span className="text-[10px] font-normal uppercase tracking-wide" style={{ color: '#4b5563' }}>TRA</span>
                            <span className="font-normal" style={{
                              color: ttInMinutes >= 0 ? '#111827' : '#9ca3af',
                              fontSize: '14px'
                            }}>
                              {ttInMinutes >= 0 ? formatTimeDisplay(ttInMinutes, customUnitTT) : '0'}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
               </>
            )}
            {/* Contedo do card inicial no modo default - Retngulo Simples */}
            {isInitial && (
               <div className={`relative h-full flex flex-col items-center justify-center ${isInitial || isFinal ? 'rounded-full' : ''}`}>
                 {/* INCIO  topo */}
                 <div className="absolute top-3 left-0 right-0 text-center">
                   <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
                     Início
                   </div>
                 </div>
                 {/* Nome do processo  centro */}
                 <div className="text-[15px] font-semibold text-slate-600 truncate px-6 text-center mt-14">
                   {effectiveNomeFluxo || 'Novo Processo'}
                 </div>
                 {/* Nmero + unidade  abaixo do nome */}
                 <div className="relative mt-6">
                   <span className="text-3xl font-bold text-slate-700">
                     {effectiveEntrada ?? 0}
                   </span>
                   <span className="absolute top-1/2 -translate-y-1/2 left-full ml-1.5 text-sm text-slate-500 whitespace-nowrap">
                     {effectiveUnidadeDemanda || '/dia'}
                   </span>
                 </div>
               </div>
            )}
            {/* Conteúdo do card final no modo default - Retângulo Simples */}
            {isFinal && (
               <div className={`flex-1 flex items-center justify-center text-center px-4 ${isInitial || isFinal ? 'rounded-sm' : ''}`}>
                 <div className="text-xl font-bold text-slate-700">
                   {effectiveNomeFluxo || 'Fim'}
                 </div>
               </div>
            )}
          </div>
        </div>
      )}
      </div>
      
      {/* Container de Notas - Fora do Card */}
      {!isInitial && !isFinal && linkedPostIts.length > 0 && (() => {
        if (postItsViewMode === 'hidden') {
          return null;
        }
        
        // Cores de fundo suaves baseadas no tipo
        const postItTypeStyles: Record<string, { bg: string; border: string }> = {
          idea: { bg: 'bg-amber-300', border: 'border-amber-300' },
          problem: { bg: 'bg-red-300', border: 'border-red-300' },
          action: { bg: 'bg-emerald-300', border: 'border-emerald-300' },
          data: { bg: 'bg-blue-300', border: 'border-blue-300' },
          risk: { bg: 'bg-purple-300', border: 'border-purple-300' },
        };
        
        if (postItsViewMode === 'compact') {
          // Modo Compact: Miniaturas de post-its reduzidos (parecidos com post-its normais, mas menores)
          // Máximo de 3 miniaturas lado a lado, depois começam a se sobrepor
          const totalPostIts = linkedPostIts.length;
          const MAX_VISIBLE_COMPACT = 3;
          const useOverlapCompact = totalPostIts > MAX_VISIBLE_COMPACT;
          // Tamanho das miniaturas: ~40% do tamanho normal dos post-its
          const COMPACT_WIDTH = Math.round(cardWidth / 6); // ~40px para card de 240px
          const COMPACT_HEIGHT = Math.round(COMPACT_WIDTH * 0.9); // 10% menor na altura
          const OVERLAP_OFFSET_COMPACT = -Math.round(COMPACT_WIDTH * 0.3); // ~30% de sobreposição
          
          return (
            <div className="w-full mt-2 z-30 transition-all duration-300 flex justify-center" style={{ overflow: 'visible' }}>
              <div className="flex flex-row items-center" style={{ gap: useOverlapCompact ? 0 : '4px' }}>
                {linkedPostIts.map((postIt, index) => {
                  const style = postItTypeStyles[postIt.type] || { bg: 'bg-gray-100', border: 'border-gray-200', icon: 'x' };
                  
                  // Considerar como "ação" tanto o tipo quanto a cor verde (padrão Lean)
                  const isActionPostIt = postIt.type === 'action' || (postIt.color && (
                    postIt.color.toLowerCase().includes('#bbf7d0') || 
                    postIt.color.toLowerCase().includes('#f0fdf4') ||
                    postIt.color.toLowerCase().includes('#dcfce7') ||
                    postIt.color.toLowerCase().includes('#86efac') ||
                    postIt.color.toLowerCase().includes('green')
                  ));

                  const shouldOverlapCompact = useOverlapCompact && index >= MAX_VISIBLE_COMPACT;
                  const overlapCountCompact = shouldOverlapCompact ? (index - MAX_VISIBLE_COMPACT + 1) : 0;
                  const translateXCompact = overlapCountCompact > 0 ? overlapCountCompact * OVERLAP_OFFSET_COMPACT : 0;
                  
                  return (
                    <div
                      key={postIt.id}
                      data-post-it="true"
                      data-post-it-id={postIt.id}
                      className={`rounded border ${style.border} ${style.bg} flex flex-col hover:z-20 hover:scale-105 transition-all cursor-help relative`}
                      style={{ 
                        width: `${COMPACT_WIDTH}px`,
                        height: `${COMPACT_HEIGHT}px`,
                        zIndex: 10 + index,
                        transform: translateXCompact !== 0 ? `translateX(${translateXCompact}px)` : 'none',
                        marginLeft: !useOverlapCompact && index > 0 ? '4px' : '0',
                        padding: '1px', // Padding reduzido para todos
                        overflow: 'hidden'
                      }}
                      title={`${postIt.type.toUpperCase()}: ${postIt.text || '(vazio)'}`}
                    >
                      {/* Header com tipo e ícone - Removido para todos para manter o padrão ActionCard profissional */}
                      
                      {/* Texto truncado - Centralizado com Fonte Adaptativa para todos */}
                      {postIt.text && (
                        <div className="flex-1 flex items-center justify-center overflow-hidden min-h-0">
                          <span style={{ 
                            textAlign: 'center',
                            color: '#374151',
                            lineHeight: '1.15', 
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap', 
                            hyphens: 'auto',
                            display: 'block',
                            width: '100%',
                            // Fonte adaptativa: diminui proporcionalmente
                            fontSize: `${Math.max(5.5, 9.5 - Math.floor(postIt.text.length / 12) * 0.4)}px`
                          }}>
                            {postIt.text}
                          </span>
                        </div>
                      )}
                      {/* Efeito de canto dobrado para parecer post-it */}
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-gray-300/40" style={{ 
                        clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
                        borderBottomRightRadius: '3px'
                      }} />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }
        
        // Modo Detailed: Usar relativeOffset quando disponível (layout automático)
        // Caso contrário, calcular layout baseado no índice (compatibilidade)
        if (postItsViewMode === 'detailed') {
          const totalPostIts = linkedPostIts.length;
          const GAP = 5; // Espaçamento fixo conforme requisitos
          const MAX_SIDE_BY_SIDE = 3; // Até 3 post-its lado a lado
          
          // Largura do post-it calculada para que 3 caibam exatamente na largura do card
          // (cardWidth - (total_de_gaps)) / 3
          const POSTIT_WIDTH = (cardWidth - (GAP * (MAX_SIDE_BY_SIDE - 1))) / MAX_SIDE_BY_SIDE;
          
          // Largura máxima permitida: a largura do próprio card
          const maxAllowedWidth = cardWidth;
          
          // Modo "Baralho/Stack" se tiver 4 ou mais
          const useOverlap = totalPostIts > MAX_SIDE_BY_SIDE;
          
          // Cálculo do deslocamento X
          // Até 3: Lado a lado fixo (POSTIT_WIDTH + GAP)
          // A partir de 4: Espaçamento dinâmico para caber no maxAllowedWidth
          const stepX = useOverlap
            ? (maxAllowedWidth - POSTIT_WIDTH) / (totalPostIts - 1)
            : POSTIT_WIDTH + GAP;
          
          // Largura total ocupada pelo grupo para centralização no card
          const groupWidth = useOverlap
            ? maxAllowedWidth
            : (totalPostIts * POSTIT_WIDTH) + (Math.max(0, totalPostIts - 1) * GAP);
            
          const startX = (cardWidth - groupWidth) / 2;
          
          // Ordenar post-its por order (se disponível) para empilhamento correto
          const sortedPostIts = [...linkedPostIts].sort((a, b) => (a.order || 0) - (b.order || 0));
          
          return (
            <div className="w-full mt-2 z-30 transition-all duration-300 flex justify-center" style={{
              overflow: 'visible',
              position: 'relative'
            }}>
              <div
                className="flex flex-row items-center"
                style={{
                  position: 'relative',
                  width: `${maxAllowedWidth}px`,
                  minHeight: '75px',
                  overflow: 'visible',
                  justifyContent: 'flex-start',
                  alignItems: 'center'
                }}
              >
                {sortedPostIts.map((postIt, index) => {
                  const style = postItTypeStyles[postIt.type] || { bg: 'bg-gray-100', border: 'border-gray-200', icon: 'x' };
                  
                  // Z-index crescente para que o mais novo fique por cima
                  const zIndex = 10 + index;
                  
                  // Verificar se este post-it está em edição
                  const isEditing = editingPostItIds.has(postIt.id);
                  
                  // Posição calculada pelo algoritmo de layout dinâmico
                  const positionX = startX + (index * stepX);
                  
                  // Escala normal (hover será controlado via onMouseEnter/onMouseLeave)
                  const scale = 1;
                  
                  return (
                    <div
                      key={postIt.id}
                      data-post-it="true"
                      data-post-it-id={postIt.id}
                      className={`${style.bg} text-xs flex flex-col min-h-[72px] flex-shrink-0 transition-all cursor-pointer relative`}
                      style={{
                        width: `${POSTIT_WIDTH}px`,
                        position: 'absolute',
                        left: `${positionX}px`,
                        zIndex: isEditing ? 1000 : zIndex,
                        padding: '1px',
                        transformOrigin: 'center center',
                        overflow: 'visible',
                        boxShadow: '0 0 0 0.5px rgba(0, 0, 0, 0.45)',
                        transition: 'transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275), z-index 0.1s ease, box-shadow 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!isEditing) {
                          e.currentTarget.style.zIndex = '150';
                          e.currentTarget.style.transform = 'scale(2.0) rotate(-1deg)';
                          e.currentTarget.style.boxShadow = '0 0 0 0.5px rgba(0, 0, 0, 0.45), 0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isEditing) {
                          e.currentTarget.style.zIndex = `${zIndex}`;
                          e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                          e.currentTarget.style.boxShadow = '0 0 0 0.5px rgba(0, 0, 0, 0.45)';
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        // 1 clique mostra no kaizenlog e vice-versa
                        if (onPostItClick) {
                          // @ts-ignore
                          onPostItClick(postIt.id, e);
                        }
                      }}
                      onPointerDown={(e) => {
                        // 1. Verificar se clicou na borda (zona de arraste)
                        const rect = e.currentTarget.getBoundingClientRect();
                        const borderSize = 8;
                        const isBorder =
                          e.clientX < rect.left + borderSize ||
                          e.clientX > rect.right - borderSize ||
                          e.clientY < rect.top + borderSize ||
                          e.clientY > rect.bottom - borderSize;

                        if (isBorder && onPostItStartDrag) {
                          e.stopPropagation();
                          const offsetX = (e.clientX - rect.left) / scale;
                          const offsetY = (e.clientY - rect.top) / scale;
                          onPostItStartDrag(postIt.id, offsetX, offsetY);
                        }
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        // Duplo clique para edição de texto
                        setEditingPostItIds(prev => {
                          const newSet = new Set(prev);
                          newSet.add(postIt.id);
                          return newSet;
                        });
                        setEditingTexts(prev => ({ ...prev, [postIt.id]: postIt.text || '' }));
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (onPostItContextMenu) {
                          onPostItContextMenu(postIt.id, e.clientX, e.clientY);
                        }
                      }}
                      title={postIt.text || '(vazio)'}
                    >
                      {/* Header: Removido para todos para manter o padrão profissional e limpo */}
                      
                      {/* Texto - modo edição ou visualização */}
                      {isEditing ? (
                        <textarea
                          id={`editing-postit-${postIt.id}`}
                          className={`flex-1 w-full text-slate-900 px-1 py-0.5 bg-transparent border-none outline-none resize-none overflow-hidden !bg-transparent !border-none !outline-none !shadow-none`}
                          style={{ 
                            background: 'transparent', 
                            backgroundColor: 'transparent',
                            border: 'none', 
                            outline: 'none', 
                            boxShadow: 'none',
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            display: 'block',
                            width: '100%',
                            height: 'auto',
                            minHeight: '20px',
                            textAlign: 'center', // Centralizado horizontalmente
                            marginTop: 'auto', // Centralizado verticalmente para todos
                            marginBottom: 'auto', // Centralizado verticalmente para todos
                            // Lógica de Fonte Adaptativa na edição
                            fontSize: `${Math.max(6, 10 - Math.floor((editingTexts[postIt.id] || postIt.text || '').length / 18) * 0.4)}px`,
                            lineHeight: '1.15'
                          }}
                          value={editingTexts[postIt.id] !== undefined ? editingTexts[postIt.id] : (postIt.text || '')}
                          rows={1}
                          onFocus={(e) => {
                            const val = e.target.value;
                            if (val.length < 3 || val === 'Nova Ação' || val === 'Nova Atividade') {
                              e.target.select();
                            } else {
                              e.target.setSelectionRange(val.length, val.length);
                            }
                            // Auto-resize imediato
                            e.target.style.height = 'auto';
                            e.target.style.height = `${e.target.scrollHeight}px`;
                          }}
                          onChange={(e) => {
                            const newText = e.target.value;
                            setEditingTexts(prev => ({ ...prev, [postIt.id]: newText }));
                            // Auto-resize
                            e.target.style.height = 'auto';
                            e.target.style.height = `${e.target.scrollHeight}px`;
                            
                            // Atualizar estado global opcionalmente aqui ou no blur
                            if (onPostItTextChange) {
                              onPostItTextChange(postIt.id, newText);
                            }
                          }}
                          onBlur={() => {
                            // Garantir que o texto final seja salvo
                            const finalText = editingTexts[postIt.id] !== undefined ? editingTexts[postIt.id] : (postIt.text || '');
                            if (onPostItTextChange && finalText !== postIt.text) {
                              onPostItTextChange(postIt.id, finalText);
                            }
                            // Limpar estado de edição
                            setEditingPostItIds(prev => {
                              const newSet = new Set(prev);
                              newSet.delete(postIt.id);
                              return newSet;
                            });
                            // Limpar texto de edição local
                            setTimeout(() => {
                              setEditingTexts(prev => {
                                const newTexts = { ...prev };
                                delete newTexts[postIt.id];
                                return newTexts;
                              });
                            }, 100);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              e.currentTarget.blur();
                            }
                            if (e.key === 'Escape') {
                              setEditingPostItIds(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(postIt.id);
                                return newSet;
                              });
                            }
                            e.stopPropagation();
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <div className="flex-1 flex items-center justify-center px-0.5 overflow-hidden">
                          <span style={{
                            color: '#111827',
                            textAlign: 'center', // Centralizado horizontalmente
                            lineHeight: '1.15', // Ajustado para 1.15 para melhor legibilidade
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap', // Preservar quebras de linha
                            hyphens: 'auto',
                            fontWeight: '500', // Peso médio para todos
                            display: 'block',
                            width: '100%',
                            // Lógica de Fonte Adaptativa: diminui conforme o texto cresce
                            fontSize: `${Math.max(5.5, 10 - Math.floor((postIt.text || '').length / 15) * 0.4)}px`
                          }}>
                            {postIt.text || (
                              <span className="text-gray-400 dark:text-slate-500 italic">Clique para digitar...</span>
                            )}
                          </span>
                        </div>
                      )}
                      
                      {/* x} Botão Impactos - Modo Kaizen */}
                      {isKaizenMode && (
                        <div
                          style={{
                            width: '100%',
                            height: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderTop: '1px solid rgba(0,0,0,0.1)',
                            backgroundColor: 'rgba(139, 92, 246, 0.2)',
                            cursor: 'pointer',
                            flexShrink: 0,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onOpenImpactPanel) {
                              onOpenImpactPanel(postIt.id);
                            }
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <div
                            style={{
                              fontSize: 8,
                              fontWeight: 700,
                              color: '#7c3aed',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                            }}
                          >
                            <Target size={8} strokeWidth={2.5} />
                            <span>Impactos</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }
        
        return null;
      })()}
    </div>
  );
};


